/**
 * Read hooks for the portal.
 *
 * Each one names the endpoint it will call when the API lands (see
 * docs/portal/design-handoff.md, "Data fetching"). Until then they resolve seed
 * data through useSeedResource, which already returns the {data, loading, error}
 * shape an async call produces.
 *
 * Access control note: the role filtering these hooks imply is presentation
 * only. The handoff is explicit that every request must re-check the caller's
 * role *and* row-level ownership server-side - a parent must never be able to
 * reach another family's records by guessing an id. Nothing in this directory
 * substitutes for that. For the live path, firestore.rules is that boundary.
 *
 * Live data: when REACT_APP_PORTAL_LIVE_DATA === 'true', useSchedule and
 * useBooking source from the Firestore adapter in ./live.js instead of seed
 * data - same payload shapes, arriving async through useSeedResource's
 * source mode, so screens see {data: null, loading: true} first and cannot
 * tell the modes apart. With the flag unset the seed path is untouched and
 * the demo needs no emulator, no network, and no signed-in user.
 *
 * Practice mode (Onboarding program v1, docs/portal/TEAM.md): useSchedule and
 * useBooking accept { practice: true }, which pins the hook to the seed source
 * even when REACT_APP_PORTAL_LIVE_DATA === 'true'. INVARIANT: onboarding /
 * practice performs ZERO Firestore writes (and no live reads either) — the
 * practice check short-circuits before isLive() is consulted, so nothing in
 * ./live.js can execute, and book() resolves locally. Why: onboarding is a
 * family learning the app by doing the real actions on the real screens, and
 * a learner must not be able to create a real booking (or spend a real
 * allowance) by practicing. Practice entries are component state, badged
 * PRACTICE, and reset on exit.
 */

import { useState } from 'react';
import useSeedResource from './useSeedResource';
import { useInvalidation } from './invalidate';
import {
  ERR,
  LiveDataError,
  approveEnrollmentRequest,
  cancelBooking,
  createBooking,
  createContractLog,
  createStaffInvite,
  declineEnrollmentRequest,
  deleteContractLog,
  fetchAllAthletes,
  fetchAllHouseholds,
  fetchAthlete,
  fetchAthletesByIds,
  fetchBookings,
  fetchBookingsBySession,
  fetchCoachAthletes,
  fetchContractLogs,
  fetchContractLogsSince,
  fetchCurrentUser,
  fetchGraceTokensByAthlete,
  fetchHousehold,
  fetchHouseholdAthletes,
  fetchHouseholdBookings,
  fetchMyEnrollmentRequest,
  fetchNoShowBookingsSince,
  fetchPackage,
  fetchPendingEnrollmentRequests,
  fetchPendingStaffInvites,
  fetchSessions,
  fetchSessionsByIds,
  fetchSessionsInRange,
  fetchStaffUsers,
  fetchTournamentResults,
  fetchTournamentResultsForSession,
  isLive,
  saveNotificationPrefs,
  saveMyPhone,
  saveTournamentResults,
  setAthletePackages,
  setBookingNoshowReason,
  setContractTier,
  setHouseholdPeriodAnchorDay,
  setSessionCoachNote,
  submitMyEnrollmentRequest,
  updateBookingStatus,
} from './live';
// Sprint 13 (contract v2.1) — the two new Part 2 modules (routing lane's
// "split new code" instruction): cancelSession/issueTokens/setHouseholdStripeIds/
// fetchTokenPeriod own token issuance + grace + the staff cancel-session
// action; fetchWaitlistBy*/leaveWaitlist/useWaitlist own the waitlist. Both
// import FROM ./live (one-directional, matching useAuthSession.js/
// useRoster.js's own existing pattern), never the other way.
import useIssueTokens, { cancelSession, fetchTokenPeriod, setHouseholdStripeIds } from './grace';
import useWaitlist, { fetchWaitlistByAthlete, fetchWaitlistByHousehold, fetchWaitlistPositions, positionOf } from './waitlist';
import useRecentNotices from './notices';
import usePush from './push';
import useSignups, { fetchLoginInvite } from './signups';
import useScholarships from './scholarships';
// 2026-10-14 (assessments spec 8): the admin "Baseline due" row reads the
// assessment windows, the module that replaced the diagnostics reads.
import { ASSESSMENTS_KEY, fetchAllAssessmentWindows } from './assessmentsLive';
import { seasonOf } from '../data/assessmentWindows';
import { withoutTestFamilies } from '../data/testFamily';
import {
  COACH,
  COACH_BLOCKS,
  COACH_BLOCKS_CONCURRENT,
  ATTENTION_LIST,
  COACH_OUTSTANDING,
  HOUSEHOLD,
  BILLING_ISSUE,
  TODAY,
  CONSENTS,
  RELATIONSHIPS,
  BOOKED_UPCOMING,
  BOOKED_PAST,
  BOOKING_CONFIRMATION,
  CANCELLED_SESSION,
  TOKENS,
  TOKENS_EXHAUSTED,
  PERIOD_ANCHOR_DAY,
  ATHLETE_PACKAGE,
  SESSION,
  ROSTER,
  GRACE_TOKEN,
  WAITLIST_ENTRY,
  // PAST_DUE_MEMBERSHIP (contract v2.1, pin H) is exported from data/seed.js
  // for the frontend lane's harness to import directly wherever it wires a
  // past_due demo state — no hook here routes it (useMembership's default
  // payload is always the active Whitfield story).
} from '../data/seed';
import {
  ELITE,
  SINGLE_TOKEN,
  TOKEN_PACKAGES,
  normalizeAnchorDay,
  packageById,
  periodFallback,
  periodFor,
  tokensFor,
  windowMonthsFor,
} from '../data/packages';
import { billingStatusOf } from '../data/billingCopy';
import { isSingleTokenId } from '../data/singleToken';
import {
  SEASON,
  SEASON_BOUNDS,
  SEASON_BY_DATE,
  capacityFor,
  datePill,
  dayLabel,
  resolveBooking,
  upcomingDates,
} from '../data/season';
import { DEFAULT_DURATION_MINUTES, ageGroupFor } from '../data/schedule';
import {
  ATHLETE,
  CODE_OF_GRIT,
  ONBOARDING,
} from '../data/athlete';
// The owner's contract rule (2026-10-28): 20 min a day, 10 under 12, derived
// per date - every family surface reads the requirement from here.
import { contractMinutesFor } from '../data/contractRule';
import {
  addDaysISO,
  ageFromDob,
  bookingOpen,
  buildContractMonth,
  buildContractMonthFromLogs,
  longDayLabel,
  monthBounds,
  openThrough,
  parseTimeToMinutes,
  pickDueDates,
  todayISO,
} from '../data/calendar';
import {
  ATHLETE_DETAIL,
  CONTRACT_HISTORY,
  LIMITED_DATA_CHECKLIST,
  NOTIFICATION_CATEGORIES,
  NOTIFICATION_NOTE,
  channelLocked,
} from '../data/parent';
import {
  OUTSTANDING,
  ADMIN_METRICS,
  ENROLLMENT_BY_PACKAGE,
  BLOCK_FILL,
  TIER_FILTERS,
  STAFF,
  STAFF_ROLES,
  AUDIT_NOTE,
  SCREENING_NOTE,
} from '../data/admin';
import { TOUR_SEED, bracketFor, deriveTourStandings } from '../data/tour';
import { SPECIALISTS, isSpecialistType, mentalCapFor } from '../data/specialists';
import { calendlyUrlFor } from '../data/calendly';
import { loginStateFor } from '../data/signups';
import { hideContractParts } from '../data/contractFlag';
import repeatWeekly, { isGroupBookable } from './repeat';
import { MARKS_LOOKAHEAD_DAYS, dayMarksFor, monthGridBounds } from '../data/calendarViews';
import { foldBeforeFirstPeriod, positionPeriodFor, tierBillingOf, withTokenStart } from '../data/billingHub';
import { sessionStarted } from '../data/sessionStart';
import { handicapLimitOf, handicapOf } from '../data/reserved';

// 2026-10-02: the academy's handicap edit (hooks/handicap.js), on this seam
// like every other staff write a screen reaches.
export { setAthleteHandicap } from './handicap';
export { default as useSeedResource } from './useSeedResource';
export { default as useAuthSession } from './useAuthSession';
export { default as useRoster } from './useRoster';
export { default as useOnboardingStatus } from './onboarding';
// Sprint 13 (contract v2.1) — already imported by name above (useWaitlist,
// useIssueTokens both need to be in this file's own scope to be usable by
// its other hooks/helpers); re-exported here so screens keep importing
// every portal hook from this one seam, same as every hook above.
// Sprint 16: hooks/billing.js derives the hub's coaching line with the same
// helper useMembership uses, so the two can never disagree.
// 2026-10-01: useScholarships is the OWNER's read of scholarshipApplications;
// only an owner's screen may mount it (hooks/scholarships.js).
export { coachingFor, useIssueTokens, usePush, useRecentNotices, useScholarships, useSignups, useWaitlist };
// 2026-10-28: Yannick's tools (hooks/mental.js, hooks/mentalLive.js); role
// 'mental' screens only - the notes reads are refused for every other role.
export { useContractBoard, useMentalAthlete, useMentalAthletes, useMentalNote } from './mental';
// 2026-10-28: the parent's contract card for one child (spec 4.3). The module
// imports useContract/usePracticeLog from this file; the cycle is safe
// because every use sits inside a hook body, never at import time.
export { useParentContract } from './parentContract';

/**
 * Harness demo states (contract v1.1): every data-bearing hook accepts
 * variant 'loading' (perpetually {data: null, loading: true}) and 'error'
 * (a user-facing failure), so the skeleton and error treatments are
 * reviewable per screen. Demo variants never touch the live source.
 */
const DEMO_DELAY_FOREVER = 2147483647; // setTimeout's max; larger fires instantly
function demoOpts(variant, message) {
  if (variant === 'loading') return { delay: DEMO_DELAY_FOREVER };
  if (variant === 'error') return { error: new Error(message) };
  return null;
}

/** id -> catalogue entry (data/specialists.js) — Phil/Yannick, never invented. */
const SPECIALIST_BY_ID = new Map(SPECIALISTS.map((sp) => [sp.id, sp]));

/**
 * The generic name a session with no explicit label falls back to —
 * 'Tour event' / 'Training block' for the generator's two original
 * types, or (Sprint 9 pin, contract v1.7) '<sessionNoun> · <name>' for a
 * specialist 1-on-1 (data/specialists.js), e.g. "Mental game session ·
 * Yannick" — NEVER 'Training block' for a phil/mental session. Shared by
 * every place a bare session type becomes a readable name: displaySession
 * below, plus useCoachDay's live day view and liveAthleteDetail's upcoming
 * list, which each had their own copy of the same ternary before this
 * sprint (useSession, the one other copy, reads only the generated season,
 * which never produces a phil/mental type — routed through this helper too
 * anyway, so a fourth divergent copy can never reappear here).
 */
export function genericSessionName(type) {
  const specialist = SPECIALIST_BY_ID.get(type);
  // "Performance with Phil" already names him; Yannick's reads "Mental game session · Yannick".
  if (specialist) return specialist.sessionNoun.includes(specialist.name) ? specialist.sessionNoun : `${specialist.sessionNoun} · ${specialist.name}`;
  return type === 'tournament' ? 'Tour event' : 'Training block';
}

/** How a season session renders on a schedule or booking list. */

function displaySession(s, today) {
  const [time, meridiem] = s.time.split(' ');
  return {
    id: s.id,
    date: s.date,
    dayLabel: dayLabel(s.date, today),
    isToday: s.date === today,
    time,
    meridiem,
    type: s.type,
    // Contract v2.0 pin J: the seed-only Saturday adult block is display-only
    // (bookable: false); every other session is bookable. Absent == true.
    bookable: s.bookable !== false,
    // A special (a holiday tournament) carries its real event name. Everything
    // else is the generic block for its type - the Workshop/Lab/Arena rotation
    // was an invented placeholder, and no made-up name ships before real
    // sessions exist to book.
    name: s.label || genericSessionName(s.type),
    // The generator assigns no coach or bay - coachId is null by design, so
    // nothing is invented here. `overflow` is retired (contract v2.0, pin J
    // - the Friday overflow block concept is gone from the locked weekly
    // schedule; the generator no longer produces the field at all).
    meta: s.special ? 'Holiday event · open to Tour competitors' : null,
    // Suggested age group (owner, 2026-09-22), derived HERE rather than in a
    // screen: this is the last place that holds the session's own date next
    // to its full time string ("4:00 PM"). Below, `time` and `meridiem` are
    // split into two fields, and a caller that rejoined them would be one
    // stale meridiem away from labelling a morning block as an afternoon one.
    // null for tournaments, specialist sessions and any unmapped block.
    ageGroup: ageGroupFor(s),
    // How long the session actually runs. Absent on a session doc written
    // before the field existed, and on every weekday block, which is why the
    // fallback is the 60-minute default rather than a guess per screen.
    durationMinutes: s.durationMinutes ?? DEFAULT_DURATION_MINUTES,
    // 2026-10-02: the handicap limit of a reserved block, or null for an
    // open one (every session before the field existed, and the whole seed
    // season). Screens tag and gate on it through data/reserved.js.
    maxHandicap: handicapLimitOf(s),
  };
}

/**
 * Shared copy for the booking waitlist note - one string, both data modes.
 */
const WAITLIST_NOTE =
  'Join the waitlist - if a spot opens the athlete is booked automatically.';

/* ------------------------------------------------------------------------- *
 * Live assembly - Firestore docs (via ./live.js) into the exact payload
 * shapes the seed path produces. Screens must not be able to tell the modes
 * apart, so any shape decision here defers to the seed code above/below it.
 * ------------------------------------------------------------------------- */

/**
 * Who the signed-in user is and what they may book against: their users doc,
 * their athlete record, its package, and every booking on file. This sprint
 * wires the athlete surface only - a parent booking for a linked athlete
 * needs an athlete picker first, which is a frontend/PM sequencing question.
 */
async function liveAthleteContext() {
  const profile = await fetchCurrentUser();
  if (!profile.athleteId) {
    throw new LiveDataError(
      ERR.INVALID,
      `users/${profile.uid} has no athleteId - the live schedule and booking ` +
        'surfaces are wired for athlete-linked accounts only in this sprint.'
    );
  }
  // Independent reads in parallel (perf wave B): the bookings need only the
  // profile's athleteId; the package and household need only the athlete.
  const [athlete, bookings] = await Promise.all([
    fetchAthlete(profile.athleteId),
    fetchBookings(profile.athleteId),
  ]);
  // The household's periodAnchorDay (contract v2.0, pin B) - every token
  // position and period badge this context feeds needs it; absent == 1.
  const [pkg, household] = await Promise.all([
    athlete.packageId ? fetchPackage(athlete.packageId) : null,
    athlete.householdId ? fetchHousehold(athlete.householdId) : null,
  ]);
  return { profile, athlete, pkg, bookings, household };
}

/**
 * The signed-in athlete's profile + athlete doc only - no package or booking
 * fetch, unlike liveAthleteContext above, since practice logging needs
 * neither. Shared by usePracticeLog's read and write paths.
 */
async function liveAthleteIdentity() {
  const profile = await fetchCurrentUser();
  if (!profile.athleteId) {
    throw new LiveDataError(
      ERR.INVALID,
      `users/${profile.uid} has no athleteId - practice logging is wired for ` +
        'athlete-linked accounts only.'
    );
  }
  const athlete = await fetchAthlete(profile.athleteId);
  return { profile, athlete };
}

/**
 * The contract hooks' athlete (owner, 2026-10-28; spec 4.3): an explicit
 * athleteId is the parent path (AthleteDetail, a kid without a login of
 * their own) and reads that athlete doc; null keeps the signed-in athlete's
 * own path above.
 */
async function liveAthleteFor(athleteId) {
  if (athleteId) return { athlete: await fetchAthlete(athleteId) };
  return liveAthleteIdentity();
}

// `packageById` (data/packages.js) is now imported directly - ONE catalogue
// (TOKEN_PACKAGES + ELITE + SINGLE_TOKEN, contract v2.0 pin A), so there is
// no second local lookup to keep in sync with it anymore.

/**
 * One athlete's token position for the CURRENT period (contract v2.0, pin B
 * - replaces deriveAllowance and its two-pool math entirely), derived by
 * counting this period's non-cancelled bookings against the package's
 * grant - per the contract there is no stored counter to drift. `anchorDay`
 * is the household's periodAnchorDay (normalizeAnchorDay already applied by
 * the caller); the period itself is periodFor(today, anchorDay) - "today",
 * not the session date, because this is a POSITION as of right now, the
 * same thing the old allowance's "used/left" pair stated for its own cycle.
 * `opts` (Sprint 13, contract v2.1, additive - every pre-existing call site
 * keeps working unchanged with the defaults below): `graceTokens` and
 * `waitlist` are now real collections (pins E/F), and `tokenPeriod` is the
 * athlete's tokenPeriods doc for THIS period when one has been issued
 * (pin C - "absent == the package's grant", still tokensFor's own default
 * when `tokenPeriod` is omitted or null). Tester report 2026-09-30: before
 * the season the period is the first (prepaid) one, and the position carries
 * withTokenStart's marks - `opts.billingStatus` is the athlete's.
 */
function deriveTokens(pkg, bookings, anchorDay, today, opts = {}) {
  if (!pkg) return null;
  const { graceTokens = [], waitlist = [], tokenPeriod = null, billingStatus } = opts;
  const period = positionPeriodFor(today, anchorDay);
  const read = (rows) => foldBeforeFirstPeriod(rows, anchorDay); // an October row spends November
  const position = tokensFor(null, pkg, read(bookings), read(waitlist), graceTokens, period.periodKey, { today, tokenPeriod });
  return withTokenStart(position, period, billingStatus);
}

/**
 * The FULL `members[].tokens` shape (contract v2.0, pin N) - deriveTokens'
 * position plus `nextPeriod: { periodKey, booked }`: the period immediately
 * after the current one, and how many non-cancelled bookings the athlete
 * already holds in it (the advance-booking count the charging rule caps at
 * one package's worth, contract v2.0 §6). Shared by useMembership's member
 * rows and useSpecialistSlots' `tokens` field so neither can disagree with
 * the other about what "next period" means for the same athlete. `opts`
 * passes straight through to deriveTokens - see its own comment.
 */
function tokensWithNextPeriod(pkg, bookings, anchorDay, today, opts = {}) {
  const position = deriveTokens(pkg, bookings, anchorDay, today, opts);
  if (!position) return null;
  const { periodEnd } = positionPeriodFor(today, anchorDay);
  const next = periodFor(addDaysISO(periodEnd, 1), anchorDay);
  const nextPeriodKey = next.periodKey;
  const booked = (bookings || []).filter(
    (b) => b && b.status !== 'cancelled' && b.periodKey === nextPeriodKey
  ).length;
  // 2026-11-01: the next period's own count beside its booked total, so a
  // booking surface can judge a next-period session by it (data/packages.js
  // positionForDate) exactly as the booking check does - its grant is the
  // issued doc when one exists (opts.nextTokenPeriod), else the package's
  // own (periodFallback), less its bookings and its waitlist holds. Elite
  // and the single token count nothing per period: null, as `left` is.
  // `used` is the period's SPEND, as tokensFor/assertPeriodTokensLeft count
  // it: a grace-charged booking (graceTokenId set) never spends a period
  // token. `booked` stays the advance-booking total (pin N).
  const used = (bookings || []).filter(
    (b) => b && b.status !== 'cancelled' && b.periodKey === nextPeriodKey && !b.graceTokenId
  ).length;
  const reserved = (opts.waitlist || []).filter((w) => w && w.periodKey === nextPeriodKey).length;
  // The single token's per-period grant is an issued doc or nothing
  // (periodFallback gives it 0), exactly as tokensFor reads the current period.
  const perPeriod = Boolean(pkg) && pkg.tokens !== null;
  const granted = perPeriod ? opts.nextTokenPeriod?.granted ?? periodFallback(pkg) : null;
  const left = perPeriod ? Math.max(0, granted - used - reserved) : null;
  return { ...position, nextPeriod: { periodKey: nextPeriodKey, periodEnd: next.periodEnd, booked, used, reserved, granted, left } };
}

/**
 * The position read live with everything that moves it (single token, ruling
 * 2026-09-29/30: purchased tokens ARE grace tokens, waitlist spots hold them,
 * a comp is a tokenPeriods doc) - fetchTokenInputs' reads, the ones the
 * booking check counts, so the waitlist read is household-scoped as the rule
 * needs. `athlete` is the athlete doc (`id`, `householdId`, `billing`). Read
 * errors propagate - never a false 0.
 */
export async function liveTokens(athlete, pkg, bookings, anchorDay, today) {
  if (!pkg) return null;
  const inputs = await fetchTokenInputs(athlete.id, athlete.householdId, anchorDay, today);
  // With the next period's count (2026-11-01), so the read fetchTokenInputs
  // makes for it is used and every meter can say what next month holds.
  return withGraceReasons(
    tokensWithNextPeriod(pkg, bookings, anchorDay, today, { ...inputs, billingStatus: billingStatusOf(athlete) }),
    inputs.graceTokens
  );
}

/**
 * The position on a home card (the parent's ChildCard, the athlete's home).
 * A single athlete's balance IS the tokens they bought, so theirs is read
 * live. A monthly token member's is read live too since the extra session
 * (owner 2026-10-10; review the same day): a bought extra is a grace token,
 * and the family page its checkout returns to must count it ("0 tokens left
 * + 1 session token") - with the waitlist holds and the issued grant, it
 * then agrees with the meters. Should those reads fail, the card keeps the
 * bookings-only figure it always showed rather than failing the home. Elite
 * has no count to read.
 */
async function homeTokens(athlete, pkg, bookings, anchorDay, today) {
  if (pkg && pkg.kind === 'single') return liveTokens(athlete, pkg, bookings, anchorDay, today);
  const bookingsOnly = () => deriveTokens(pkg, bookings, anchorDay, today, { billingStatus: billingStatusOf(athlete) });
  if (!pkg || pkg.kind !== 'tokens') return bookingsOnly();
  try {
    return await liveTokens(athlete, pkg, bookings, anchorDay, today);
  } catch (err) {
    return bookingsOnly();
  }
}

/** Sort key: chronological, then block order (session ids end in the block index). */
/**
 * Yannick's frequency line (pin K): non-cancelled 'mental' bookings in
 * the given month (K04: the slot's, default today's) against the
 * owner-tunable knob. A frequency rule, never an allowance - `limit` null
 * means tokens are the only limit.
 */
function coachingFor(bookings, today, pkg = null, monthISO = today.slice(0, 7)) {
  const month = monthISO;
  const used = (bookings || []).filter(
    (b) => b && b.type === 'mental' && b.status !== 'cancelled' && (b.date || '').slice(0, 7) === month
  ).length;
  // v2.0.1 (Sprint 18): per package - Elite two a month, everyone else one.
  const limit = mentalCapFor(pkg);
  return { used, limit, capReached: limit != null && used >= limit };
}

/**
 * data/packages.js#tokensFor's own `grace` output is `{ id, expiresAt }`
 * only (that file is db/routing-shared and not this lane's to grow); the
 * hook seam wants `reason` on it too (contract v2.1: "grace: [{ id,
 * expiresAt, reason }]"), so it is joined back on here from the raw
 * graceTokens rows the caller already fetched - never re-derived, just
 * carried through.
 */
function withGraceReasons(tokens, rawGraceTokens) {
  if (!tokens || !tokens.grace || !tokens.grace.length) return tokens;
  const byId = new Map((rawGraceTokens || []).map((g) => [g.id, g]));
  // reason + sourceSessionId ride along so the grace line can name the
  // cancelled block ("the Nov 11 block was cancelled"), never a stored copy.
  return {
    ...tokens,
    grace: tokens.grace.map((g) => ({
      ...g,
      reason: byId.get(g.id)?.reason ?? null,
      sourceSessionId: byId.get(g.id)?.sourceSessionId ?? null,
    })),
  };
}

function byDateThenId(a, b) {
  return a.date === b.date ? (a.id < b.id ? -1 : 1) : a.date < b.date ? -1 : 1;
}

/**
 * What a token position is derived from besides bookings (contract v2.1):
 * the athlete's bonus tokens, the waitlist entries holding a token, and this
 * period's issued grant. The booking check (live.js createBooking) counts
 * all three, so every booking surface reads them too (audit 2026-09-30) -
 * otherwise a held token showed as "1 left" and a bonus token could not be
 * spent once the period read zero. `householdId` scopes the waitlist read
 * for a parent, as the rule needs.
 */
async function fetchTokenInputs(athleteId, householdId, anchorDay, today) {
  const { periodKey, periodEnd } = positionPeriodFor(today, anchorDay);
  const [graceTokens, waitlist, tokenPeriod, nextTokenPeriod] = await Promise.all([
    fetchGraceTokensByAthlete(athleteId),
    fetchWaitlistByAthlete(athleteId, { householdId }),
    fetchTokenPeriod(athleteId, periodKey),
    // The next period's issued grant, when ops or Stripe already wrote one
    // (2026-11-01: the booking surfaces judge a next-period session by that
    // period's own count, so they read what the booking check reads).
    fetchTokenPeriod(athleteId, periodFor(addDaysISO(periodEnd, 1), anchorDay).periodKey),
  ]);
  return { graceTokens, waitlist, tokenPeriod, nextTokenPeriod };
}

/**
 * Waitlist entries resolved into rows shaped like a booking row (contract
 * v2.1, pin F: "waitlisted items merged... with waitlistPosition"), shared
 * by useSchedule and useHouseholdReservations' live branches so the two can
 * never disagree about what a waitlisted row looks like. The place in line
 * is ONE fetchWaitlistPositions() call for every session among the entries
 * (the waitlistPositions callable: the order promotion uses, the caller's
 * own athletes only) - null when it is unknown, never a failed list.
 * `sessionsById` is a pre-fetched map the caller already built (so this
 * never issues its own redundant fetchSessionsByIds call).
 */
async function resolveWaitlistRows(entries, sessionsById, today, anchorDay, currentPeriodKey) {
  if (!entries.length) return [];
  const positions = await fetchWaitlistPositions(entries.map((e) => e.sessionId));
  return entries
    .map((e) => {
      const s = sessionsById.get(e.sessionId);
      if (!s) return null; // dropped, same null-resolve rule as a booking whose session vanished
      const periodKey = periodFor(s.date, anchorDay).periodKey;
      const specialist = SPECIALIST_BY_ID.get(s.type);
      return {
        ...displaySession(s, today),
        badge: { tone: 'yellow', label: 'Waitlisted' },
        bookingId: null,
        status: 'waitlisted',
        waitlistPosition: positionOf(positions, e.sessionId, e.athleteId),
        athleteId: e.athleteId,
        // Shape parity with reservationRow's own confirmed-booking rows -
        // durationMinutes/instructor read the same way regardless of status.
        instructor: specialist ? specialist.name : null,
        durationMinutes: s.durationMinutes ?? (specialist ? specialist.durationMinutes : DEFAULT_DURATION_MINUTES),
        cancellable: false,
        source: 'portal',
        periodKey,
        nextPeriod: periodKey > currentPeriodKey,
      };
    })
    .filter(Boolean);
}

/** Live payload for useSchedule - same shape as the seed branch produces. */
async function liveSchedule(today) {
  const ctx = await liveAthleteContext();
  // Contract v2.1, pin G: cancelled bookings are no longer dropped from the
  // list - they render with cancelledBy/cancelReason (the cancelled row
  // state and its "why" line need something real to read) rather than
  // vanishing the moment a booking is cancelled.
  const anchorDay = normalizeAnchorDay(ctx.household?.periodAnchorDay);
  // The period the token meter reads (November before the season), so a
  // November booking is never "Next period" while the meter spends it.
  const currentPeriodKey = positionPeriodFor(today, anchorDay).periodKey;

  const [waitlistEntries, graceTokens, tokenPeriod] = await Promise.all([
    fetchWaitlistByAthlete(ctx.athlete.id),
    fetchGraceTokensByAthlete(ctx.athlete.id),
    fetchTokenPeriod(ctx.athlete.id, currentPeriodKey),
  ]);

  // Join bookings AND waitlist entries to their session docs in ONE fetch -
  // time, label and special flags live on the session, and an item whose
  // session no longer exists is dropped rather than rendered, mirroring the
  // seed's null-resolve rule.
  const sessionsById = new Map(
    (
      await fetchSessionsByIds([
        ...ctx.bookings.map((b) => b.sessionId),
        ...waitlistEntries.map((w) => w.sessionId),
      ])
    ).map((s) => [s.id, s])
  );
  const resolve = (b) => {
    const s = sessionsById.get(b.sessionId);
    if (!s) return null;
    // Computed from the SESSION's own date (contract v2.0 §6: a booking is
    // charged against the period its session date falls in) rather than
    // trusted off the stored booking field, so display can never disagree
    // with what createBooking itself derived at write time.
    const periodKey = periodFor(s.date, anchorDay).periodKey;
    // Sprint 20 (spec 6.1): a Calendly-sourced booking is cancelled from Calendly's email, never here (My Schedule shows the note instead of Cancel).
    const source = b.source ?? 'portal';
    return {
      ...displaySession(s, today),
      badge:
        b.status === 'confirmed'
          ? { tone: 'green', label: 'Confirmed' }
          : b.status === 'attended'
          ? { tone: 'neutral', label: 'Attended' }
          : b.status === 'noshow'
          ? { tone: 'red', label: 'No-show' }
          : b.status === 'cancelled'
          ? { tone: 'red', label: 'Cancelled' }
          : null,
      // Sprint 9 pin: cancel(bookingId) below needs the real booking id
      // (never the session id displaySession already carries as `id`),
      // and `cancellable` is the exact pinned formula - status
      // 'confirmed' AND still in the future. Past items compute false
      // here for free (their date is always < today).
      bookingId: b.id,
      status: b.status,
      source,
      cancellable: b.status === 'confirmed' && b.date > today && source !== 'calendly',
      singleToken: isSingleTokenId(b.graceTokenId),
      // Contract v2.1, pin G: present on a cancelled row only - absent
      // (null) on every other status, never invented.
      cancelReason: b.cancelReason ?? null,
      cancelledBy: b.cancelledBy ?? null,
      periodKey,
      nextPeriod: periodKey > currentPeriodKey,
    };
  };

  const resolved = ctx.bookings.map(resolve).filter(Boolean);
  const waitlistRows = await resolveWaitlistRows(waitlistEntries, sessionsById, today, anchorDay, currentPeriodKey);
  const upcoming = resolved.filter((r) => r.date >= today).concat(waitlistRows);
  const past = resolved.filter((r) => r.date < today);
  upcoming.sort(byDateThenId);
  past.sort((a, b) => -byDateThenId(a, b)); // most recent first

  return {
    sessions: upcoming,
    past,
    // Academy-cancellation banners need a cancellation reason the contract
    // does not carry yet - flagged in the routing report, null until then.
    cancelled: null,
    // `allowance` -> `tokens` (contract v2.0): the two-pool shape is
    // retired; this is the athlete's token position for the CURRENT
    // period, same shape as useMembership's members[].tokens minus
    // nextPeriod (a household/all-members concept, not a per-schedule one).
    // Contract v2.1: now derived with the athlete's real grace tokens and
    // waitlist entries (pin B's `reserved`), not the Part 1 empty arrays.
    tokens: withGraceReasons(
      deriveTokens(ctx.pkg, ctx.bookings, anchorDay, today, { graceTokens, waitlist: waitlistEntries, tokenPeriod, billingStatus: billingStatusOf(ctx.athlete) }),
      graceTokens
    ),
  };
}

/**
 * Who is booking (Sprint 6 pin, QA #2): an athlete's own account, or a parent
 * booking for one of their linked athletes. Distinct from liveAthleteContext,
 * which assumes an athlete-linked account throws otherwise — a parent user
 * has no athleteId of their own, and which child they are booking for is a
 * per-call choice (book(slot, { athleteId })), not something resolved once
 * up front here.
 */
async function liveBookingIdentity() {
  const profile = await fetchCurrentUser();
  if (profile.athleteId) return { role: 'athlete', profile };
  if (profile.householdId) return { role: 'parent', profile };
  throw new LiveDataError(
    ERR.INVALID,
    `users/${profile.uid} has neither athleteId nor householdId - the booking ` +
      'surface is wired for athlete-linked or parent accounts only.'
  );
}

/**
 * The widest booking window any package offers right now (Elite's two
 * months, owner ruling 2026-11-02) - the safe fetch bound for a caller whose
 * specific package is not yet known (a parent who has not picked a child).
 * Computed from the catalogue rather than hardcoded so a future window
 * change here needs no second edit.
 */
const MAX_WINDOW_MONTHS = Math.max(...TOKEN_PACKAGES.map(windowMonthsFor), windowMonthsFor(ELITE), windowMonthsFor(SINGLE_TOKEN));
/** Seed mode's stand-in for a month of session days (upcomingDates counts dates, not days). */
const SEED_DATES_PER_MONTH = 30;

/**
 * Live payload for useBooking, plus the identity the book() action needs.
 * The hook strips `identity` off before it reaches the screen - the payload
 * the screen sees is shape-identical to the seed branch.
 *
 * An athlete caller gets their own token position up front, same as before.
 * A parent caller has not chosen a child yet at this point - `tokens` is
 * null rather than any one child's number (never invented, never picked for
 * them); the screen's child picker reads each child's own token position
 * from useHouseholdAthletes(), which already returns it per athlete.
 *
 * Contract v2.0 pin D: the fetch window is no longer a fixed 7 days - it is
 * openThrough(now, windowMonthsFor(pkg)), the athlete's own package window
 * (through next month; two months for Elite). A parent caller has no specific child's package
 * yet, so the fetch uses the WIDEST window any package offers (MAX_WINDOW_
 * MONTHS, currently Elite's two) - a superset that never under-fetches what a
 * child chosen afterward might need; per-day locking past THAT child's own,
 * narrower window is the screen's job once a child is picked (it already
 * has each child's packageId via useHouseholdAthletes).
 *
 * `withSlots: false` (perf wave B) is the identity-only path for a caller
 * that needs book() and nothing else (SpecialistBooking): no sessions range
 * read, no package/bookings/household chain - the same `identity` book()
 * reads, and an empty payload in the same shape.
 */
async function liveBooking(today, { withSlots = true } = {}) {
  const who = await liveBookingIdentity();
  if (!withSlots) {
    const identity =
      who.role === 'athlete'
        ? {
            role: 'athlete',
            athleteId: who.profile.athleteId,
            householdId: (await fetchAthlete(who.profile.athleteId)).householdId,
          }
        : { role: 'parent', householdId: who.profile.householdId };
    return {
      dates: [],
      slots: [],
      tokens: null,
      seasonNote: null,
      confirmation: { ...BOOKING_CONFIRMATION, email: who.profile.email },
      identity,
    };
  }

  let windowMonths = MAX_WINDOW_MONTHS;
  let tokens = null;
  let identity;
  if (who.role === 'athlete') {
    // Independent reads in parallel (perf wave B), same pattern as
    // liveAthleteContext: athlete + bookings, then package + household.
    const [athlete, bookings] = await Promise.all([
      fetchAthlete(who.profile.athleteId),
      fetchBookings(who.profile.athleteId),
    ]);
    const [pkg, household] = await Promise.all([
      athlete.packageId ? fetchPackage(athlete.packageId) : null,
      athlete.householdId ? fetchHousehold(athlete.householdId) : null,
    ]);
    const anchorDay = normalizeAnchorDay(household?.periodAnchorDay);
    windowMonths = windowMonthsFor(pkg);
    // The position the booking check enforces: waitlist holds and bonus
    // tokens counted (fetchTokenInputs) - a single athlete's purchased
    // tokens among them.
    const inputs = await fetchTokenInputs(athlete.id, athlete.householdId, anchorDay, today);
    // With the next period's count (2026-11-01): an athlete's own Book a
    // Session judges a next-period session by that period, as a parent's does.
    tokens = withGraceReasons(
      tokensWithNextPeriod(pkg, bookings, anchorDay, today, { ...inputs, billingStatus: billingStatusOf(athlete) }),
      inputs.graceTokens
    );
    identity = { role: 'athlete', athleteId: athlete.id, householdId: athlete.householdId };
  } else {
    identity = { role: 'parent', householdId: who.profile.householdId };
  }

  // Group flow only (surface scan 2026-09-11, blocker D1): specialist
  // slots have their own screen — leaking them here crashed the
  // two-pool allowance math when that existed, and still does not belong
  // on this list under the token model either (SpecialistBooking is the
  // one place a specialist slot is offered).
  const sessions = (await fetchSessionsInRange(today, openThrough(new Date(), windowMonths))).filter(
    (s) => isGroupBookable(s)
  );
  sessions.sort(byDateThenId);

  const dates = [...new Set(sessions.map((s) => s.date))].map(datePill);
  const slots = sessions.map((s) => ({
    ...displaySession(s, today),
    time: s.time,
    capacity: capacityFor(s),
    note: WAITLIST_NOTE,
  }));

  const seasonNote =
    dates.length && dates[0].iso > today
      ? `The 26/27 season opens ${dayLabel(dates[0].iso, today)} — these are the first bookable blocks.`
      : null;

  return {
    dates,
    slots,
    tokens,
    seasonNote,
    // Same shape as the seed confirmation; the email is the real account's,
    // and name/when are filled by the screen from the booked slot.
    confirmation: { ...BOOKING_CONFIRMATION, email: who.profile.email },
    identity,
  };
}

/**
 * The seed cast's one athlete id (matches data/tour.js's TOUR_SEED_NAMES and
 * the real Whitfield athleteId elsewhere in this repo — never a fresh
 * invented id). Seed mode has no real booking docs to read an id off of, so
 * cancel(bookingId) needs SOMETHING to echo back; this mirrors the live
 * keyspace's own `{athleteId}_{sessionId}` formula (docs/portal/TEAM.md
 * "Booking id is `{athleteId}_{sessionId}`") purely for shape parity with
 * live mode. It is never looked up against anything - seed's cancel() is a
 * local no-op (see useSchedule below).
 */
const SEED_ATHLETE_ID = 'jordan';
const SEED_HOUSEHOLD_ID = 'whitfield'; // the seed household's id (hooks/billing.js seedBillingHub, :1966)
function seedBookingId(sessionId) {
  return `${SEED_ATHLETE_ID}_${sessionId}`;
}

/**
 * GET /schedule/availability + GET /athletes/:id/tokens (04).
 *
 * Seed mode: the athlete's bookings are { date, block } references resolved
 * against the generated season, so what My Schedule shows can never contradict
 * what Book a Session offers - same session objects, same types, same times. A
 * reference into a closure resolves to null and is dropped rather than
 * rendered.
 *
 * Live mode: bookings/{athleteId} joined to their session docs, same shape.
 * The demo-state `variant` knob only applies to seed data - live data shows
 * whatever is real.
 *
 * `practice: true` (onboarding) pins this hook to the seed source regardless
 * of the live flag — see the practice-mode invariant in the file header.
 */
export function useSchedule({ variant = 'upcoming', today = todayISO(), practice = false } = {}) {
  // Practice short-circuits before isLive(): with practice set, the live
  // source below is unreachable and ./live.js never runs.
  const live = !practice && isLive();
  // Post-write invalidation seam (Sprint 6 pin): re-run after any booking
  // write, not just one made through this hook instance.
  const bookingsGen = useInvalidation('bookings');
  // Contract v2.0 pin N: items gain periodKey + nextPeriod, same as the live
  // branch - the seed athlete's own package/anchor stand in for the real
  // household's.
  const currentPeriodKey = positionPeriodFor(today, PERIOD_ANCHOR_DAY).periodKey;

  const resolve = (refs) =>
    refs
      .map((ref) => {
        const s = resolveBooking(ref);
        if (!s) return null;
        const periodKey = periodFor(s.date, PERIOD_ANCHOR_DAY).periodKey;
        return {
          ...displaySession(s, today),
          badge: ref.badge ?? null,
          // Sprint 9 pin: same three fields the live branch's resolve()
          // adds, so cancel()/`cancellable` behave identically in both
          // modes. Every BOOKED_UPCOMING/BOOKED_PAST reference is a
          // confirmed booking by construction (there is no seed concept of
          // attended/no-show/cancelled bookings yet), so `status` is fixed
          // and `cancellable` reduces to the same date check the live
          // branch runs.
          bookingId: seedBookingId(s.id),
          status: 'confirmed',
          cancellable: s.date > today,
          // Contract v2.1, pin G: shape parity with the live branch's
          // resolve() - always present, null on every seed row (the demo
          // has no cancelled-booking story for the seed athlete; nico's
          // WAITLIST_ENTRY below is the harness's cancellation-adjacent
          // demo state instead).
          cancelReason: null,
          cancelledBy: null,
          periodKey,
          nextPeriod: periodKey > currentPeriodKey,
        };
      })
      .filter(Boolean);

  // Contract v2.1, pin F: WAITLIST_ENTRY is nico's, not the seed's signed-in
  // athlete (jordan/SEED_ATHLETE_ID) - jordan personally has nothing on any
  // waitlist in the demo story, so useSchedule's OWN seed branch merges in
  // nothing here (own-records-only holds even in seed mode); nico's row
  // shows up where nico actually lives - useHouseholdReservations' seed
  // branch, below. The merge CODE PATH itself (resolveWaitlistRows) is
  // exercised live for whichever real athlete is signed in.
  const sessions = variant === 'empty' ? [] : resolve(BOOKED_UPCOMING);
  const past = variant === 'empty' ? [] : resolve(BOOKED_PAST);
  const cancelled = variant === 'cancelled' ? CANCELLED_SESSION : null;

  const demo = demoOpts(variant, "Your schedule didn't load.");
  const state = useSeedResource(
    demo || live ? null : { sessions, past, cancelled, tokens: TOKENS },
    demo ??
      (live ? { source: () => liveSchedule(today), deps: ['schedule', today, bookingsGen] } : undefined)
  );

  /**
   * Cancel a booking off this hook's own list (Sprint 9 pin). Additive to
   * the {data, loading, error} contract, same idiom as useBooking's book():
   * live mode calls the real transaction (live.js's cancelBooking, which
   * bumps 'bookings' and 'sessions' itself on success - the invalidation
   * seam above re-runs this hook, and every other mounted hook watching
   * either collection, with no extra wiring here); seed mode is a LOCAL
   * NO-OP ECHO, matching useBooking's book() in seed mode - there is no real
   * document to mutate, so it resolves without touching the static seed
   * arrays.
   */
  const cancel = async (bookingId, opts) => {
    if (!live) return { id: bookingId, status: 'cancelled', simulated: true };
    return cancelBooking({ bookingId, ...opts }); // opts: a series cancel's { cancelledVia, silent } (cancelSeries.js)
  };

  return { ...state, cancel };
}

/**
 * GET /schedule/availability + GET /athletes/:id/tokens (05).
 *
 * Availability comes from the generated season, so what the screen shows is the
 * real weekly pattern with closures applied. Only the visible window is passed
 * through the seam - the screen renders one day at a time.
 *
 * Contract v2.0: ONE token pool - `variant` still offers an exhausted demo
 * state (TOKENS_EXHAUSTED), but there is no longer a per-pool split to
 * express (the old two independent "limit" states are gone with the pools
 * that produced them).
 *
 * Live mode sources sessions (through the athlete's own package window,
 * pin D) and the derived token position from Firestore, and the returned
 * `book(slot)` persists a booking through the adapter - firestore.rules
 * re-checks the session's real type and the caller's own token grant. In
 * seed mode book(slot) resolves locally, matching today's screen behavior
 * (the screen keeps the booked slot in component state).
 *
 * `practice: true` (onboarding) pins this hook to the seed source regardless
 * of the live flag, and book(slot) resolves locally exactly as seed mode
 * does — see the practice-mode invariant in the file header.
 *
 * Sprint 6 pin (QA #2): a parent account can book too, for a linked athlete
 * they choose. `book(slot, { athleteId })` takes the chosen child at call
 * time; `bookingFor` ('athlete' | 'parent' | null while loading/seed) tells
 * the screen which case it is in, so it knows whether to show a child
 * picker — the picker's own choices come from the existing
 * useHouseholdAthletes(), not duplicated here.
 */
export function useBooking({ variant = 'open', today = todayISO(), practice = false, withSlots = true } = {}) {
  // Practice short-circuits before isLive(): with practice set, the live
  // source below is unreachable, ./live.js never runs, and book() takes the
  // local (seed) branch — a practice booking cannot become a real one.
  const live = !practice && isLive();
  // Who the booking is for, captured when the live source resolves - a
  // parent's caller identity carries no athleteId until book() is called
  // with one. State (not a ref): `bookingFor` below is derived from it and
  // must trigger a render when the live source resolves.
  const [identity, setIdentity] = useState(null);

  // Contract v2.0 pin D: the fixed "next 7 days" is retired - the seed
  // athlete's own package window (windowMonthsFor, data/packages.js), a
  // month as thirty session-bearing dates, is the count upcomingDates
  // gathers, the seed analog of the live fetch's openThrough() bound.
  const dates = upcomingDates(SEASON, today, SEED_DATES_PER_MONTH * windowMonthsFor(ATHLETE_PACKAGE)).map(datePill);

  const slots = dates.flatMap((d) =>
    (SEASON_BY_DATE.get(d.iso) ?? []).map((s) => ({
      ...displaySession(s, today),
      // The screen splits the day list on this; displaySession's date is the
      // same value, kept under both names until the screen is reworked.
      time: s.time,
      capacity:
        variant === 'full'
          ? { state: 'full', label: 'Full' }
          : capacityFor(s),
      note: WAITLIST_NOTE,
    }))
  );

  // `allowance` -> `tokens` (contract v2.0): one pool, so 'limitTraining'
  // and 'limitTournament' now land on the SAME exhausted position - the
  // variant names stay (StatesHarness/BookSession still pass them) but both
  // map to the one "no tokens left" demo state.
  const tokens = {
    open: TOKENS,
    full: TOKENS,
    confirmed: TOKENS,
    limitTraining: TOKENS_EXHAUSTED,
    limitTournament: TOKENS_EXHAUSTED,
  }[variant] || TOKENS;

  // Before the season opens, the first bookable day is weeks out - say so
  // rather than presenting November dates as if they were this week.
  const seasonNote =
    dates.length && dates[0].iso > today
      ? `The 26/27 season opens ${dayLabel(dates[0].iso, today)} — these are the first bookable blocks.`
      : null;

  // Post-write invalidation seam (Sprint 6 pin): a booking changes both
  // collections - re-run after either bumps, not just one made through this
  // hook instance.
  const bookingsGen = useInvalidation('bookings');
  const sessionsGen = useInvalidation('sessions');

  const demo = demoOpts(variant, "Open blocks didn't load.");
  const state = useSeedResource(
    demo || live ? null : { dates, slots, tokens, seasonNote, confirmation: BOOKING_CONFIRMATION },
    demo ??
      (live
        ? {
            source: async () => {
              const { identity: id, ...payload } = await liveBooking(today, { withSlots });
              setIdentity(id);
              return payload;
            },
            deps: ['booking', today, bookingsGen, sessionsGen, withSlots],
          }
        : undefined)
  );

  /**
   * Persist a booking for a slot off this hook's `data.slots`. Additive to the
   * {data, loading, error} contract - existing screens ignore it; wiring the
   * confirm tap to `await book(slot)` is the frontend lane's move.
   *
   * `athleteId` is required when the caller is a parent (bookingFor ===
   * 'parent') - the screen must have a child selected before calling book().
   * An athlete caller ignores the option (they can only ever book themselves).
   *
   * Reason plumbing (Sprint 11 pin C/G, contract v1.9; reasons updated
   * Sprint 12, contract v2.0 pin B/D/K): book() takes no try/catch of its
   * own on purpose - a rejection from createBooking's
   * assertWithinPeriodCap (live.js) is a LiveDataError whose `.reason` is
   * 'outside-window', 'no-tokens-left' or 'cap-reached' (Yannick's monthly
   * frequency knob only), and wrap() returns that SAME instance unchanged
   * for anything already a LiveDataError, so the reason reaches the caller
   * exactly as thrown - a screen may read `err.reason` directly (no import
   * needed, same plain-object convention `err.message` already relies on)
   * alongside the existing plain-language `err.message` fallback for every
   * other case.
   *
   * `joinWaitlist` (audit 2026-09-30) is the Join waitlist button's own
   * request: only then does a full session resolve { status: 'waitlisted' },
   * with the athlete's place from the waitlistPositions callable (null when
   * it is unknown). Without it a full session rejects with reason 'full' and
   * nothing is written.
   */
  const book = async (slot, { athleteId, attendee, joinWaitlist = false } = {}) => {
    if (!live) return slot;
    if (!identity) {
      throw new LiveDataError(
        ERR.INVALID,
        'book() called before the booking data finished loading.'
      );
    }
    if (identity.role === 'parent' && !athleteId) {
      throw new LiveDataError(
        ERR.INVALID,
        'book() needs the child to book for - pass { athleteId } for a parent account.'
      );
    }
    const booking = {
      athleteId: identity.role === 'parent' ? athleteId : identity.athleteId,
      sessionId: slot.id,
      date: slot.date,
      type: slot.type,
      householdId: identity.householdId,
      attendee,
    };
    const result = await (joinWaitlist ? createBooking(booking, { waitlistIfFull: true }) : createBooking(booking));
    if (result && result.status === 'waitlisted') {
      return { ...result, position: positionOf(await fetchWaitlistPositions([slot.id]), slot.id, booking.athleteId) };
    }
    return result;
  };

  // Repeat weekly lives in hooks/repeat.js (review 2026-09-30, the 500-line rule).
  const bookRecurring = (slot, opts) =>
    live ? repeatWeekly(identity, slot, opts) : Promise.resolve({ booked: [], skipped: [], windowEnd: null, next: null, simulated: true });

  return { ...state, book, bookRecurring, bookingFor: identity ? identity.role : null };
}

/**
 * Bookable sessions for one calendar month, grouped by date - the shape the
 * new Book a Session month calendar (Sprint 5 UI ruling) reads instead of a
 * flat 7-day list. Cancelled sessions are excluded here so a screen never has
 * to re-check status. Shared by the seed and live branches below.
 */
function groupSessionsByDate(sessions, today) {
  const byDate = new Map();
  for (const s of sessions) {
    if (s.status === 'cancelled') continue;
    const list = byDate.get(s.date);
    // The pinned month-session shape keeps the raw numbers alongside the
    // display fields: the booking sheet computes spots-left from
    // capacity/booked, which displaySession (a list formatter) drops.
    // ONE canonical time shape for month rows: `time` is the doc's FULL
    // string ("9:00 AM") and meridiem is REMOVED — carrying the full string
    // next to displaySession's stale split meridiem left two incompatible
    // shapes under the same field names (code review 2026-09-04): consumers
    // split `time` themselves.
    const { meridiem: _split, ...display } = displaySession(s, today);
    const row = { ...display, time: s.time, capacity: s.capacity, booked: s.booked };
    if (list) list.push(row);
    else byDate.set(s.date, [row]);
  }
  return [...byDate.keys()].sort().map((date) => ({ date, sessions: byDate.get(date) }));
}

/**
 * Live payload for useMonthSessions - a single date-range query (>= start,
 * <= end, both on the 'date' field) plus orderBy('date'), so only the
 * automatic single-field index is needed, never a composite one. The range
 * is the month's whole GRID (Mon of its first row .. Sun of its last), so a
 * week straddling two months has all seven days (2026-09-30), plus
 * MARKS_LOOKAHEAD_DAYS that only the day marks see (their sync horizon).
 */
async function liveMonthSessions(monthISO, today) {
  const { label } = monthBounds(monthISO);
  const { start, end } = monthGridBounds(monthISO);
  // Same group-flow filter as liveBooking above: the month calendar feeds
  // Book a Session and the coach's own day — specialist slots live on
  // /portal/coaching and /portal/my-sessions instead.
  // The range read and the caller's identity are independent (perf wave B);
  // see the waitlist note below for why the identity errors closed to null.
  const [all, who] = await Promise.all([
    fetchSessionsInRange(start, addDaysISO(end, MARKS_LOOKAHEAD_DAYS)),
    liveBookingIdentity().catch(() => null),
  ]);
  // Day marks judge EVERY type (a Phil-only day is not closed), so they come
  // from the full read, before the group filter - no second query. The
  // lookahead rows stop there: the calendar itself still gets only the grid.
  const dayMarks = dayMarksFor(all, start, end);
  const sessions = all.filter((s) => s.date <= end && !isSpecialistType(s.type));
  sessions.sort(byDateThenId);
  const days = groupSessionsByDate(sessions, today);

  // Contract v2.1, pin F: annotate matching rows with the CALLER's OWN
  // waitlist state - an athlete's own entries, or every entry across a
  // parent's household (fetchWaitlistByHousehold, one query for every
  // member) - never another family's, the same own-records-only scope
  // every other read in this file keeps. Staff/coach callers (this view
  // is readable by every role) simply get no annotation - the identity
  // resolution errors closed to null instead of failing the whole month.
  if (who) {
    const entries = await (who.role === 'athlete'
      ? fetchWaitlistByAthlete(who.profile.athleteId)
      : fetchWaitlistByHousehold(who.profile.householdId));
    const bySession = new Map();
    for (const e of entries) bySession.set(e.sessionId, [...(bySession.get(e.sessionId) ?? []), e]);
    // One positions call for every waitlisted row (the waitlistPositions
    // callable; empty when it is unavailable), then annotate the rows in
    // place. `waitlistBy` is WHO waits and their place - a parent's two
    // children can wait on one session, and the card must know which.
    const rows = days.flatMap((day) => day.sessions).filter((row) => bySession.has(row.id));
    const positions = rows.length ? await fetchWaitlistPositions(rows.map((row) => row.id)) : {};
    rows.forEach((row) => {
      const waiting = bySession.get(row.id);
      row.waitlisted = true;
      row.waitlistPosition = positionOf(positions, row.id, waiting[0].athleteId);
      row.waitlistBy = Object.fromEntries(waiting.map((e) => [e.athleteId, positionOf(positions, row.id, e.athleteId)]));
    });
  }
  return { month: label, days, dayMarks };
}

/**
 * GET /schedule/month?month=:monthISO (Sprint 5) - the booking calendar's
 * data source: tap a date, see that day's sessions, pick one. `monthISO` is
 * 'yyyy-MM' or any 'yyyy-MM-dd' within the month; defaults to the current
 * month. Seed: the generated season, filtered to the requested month - the
 * same SEASON useBooking reads, so the two surfaces cannot disagree.
 *
 * Both branches cover the month's whole grid (monthGridBounds) and return
 * `dayMarks` (iso -> 'tournament'|'closed', data/calendarViews.js) beside
 * `days`, derived from the same read (plus its MARKS_LOOKAHEAD_DAYS, which
 * only the marks see). The seed season holds no specialist sessions, so a
 * seed closure is simply a season day the generator skipped.
 */
export function useMonthSessions(monthISO, { practice = false } = {}) {
  // Practice pins the seed source (onboarding invariant, TEAM.md): the
  // walkthrough's booking step must work signed-out with zero network.
  const live = !practice && isLive();
  const today = todayISO();
  const resolvedMonth = monthISO || today;
  // Post-write invalidation seam (Sprint 6 pin): a booking changes
  // sessions.booked - re-run so spots-left stays correct after a write made
  // anywhere, not just through this hook instance.
  const sessionsGen = useInvalidation('sessions');
  // A waitlist join or leave changes a row's "On the waitlist" state.
  const waitlistGen = useInvalidation('waitlist');

  const seedValue = () => {
    const { label } = monthBounds(resolvedMonth);
    const { start, end } = monthGridBounds(resolvedMonth);
    const readEnd = addDaysISO(end, MARKS_LOOKAHEAD_DAYS);
    const read = SEASON.filter((s) => s.date >= start && s.date <= readEnd);
    const inGrid = read.filter((s) => s.date <= end);
    return { month: label, days: groupSessionsByDate(inGrid, today), dayMarks: dayMarksFor(read, start, end) };
  };

  return useSeedResource(
    live ? null : seedValue(),
    live
      ? {
          source: () => liveMonthSessions(resolvedMonth, today),
          deps: ['month-sessions', resolvedMonth, sessionsGen, waitlistGen],
        }
      : undefined
  );
}

/**
 * Seed pattern for useSpecialistSlots (Sprint 9 pin; window rewritten
 * Sprint 12, contract v2.0 pin D) — a DETERMINISTIC, believable schedule,
 * not real production data (production comes from the Google Calendar sync;
 * the db lane's emulator seed hand-adds slots following this SAME weekday/
 * time pattern so the demo and the emulator tell the same story). Yannick
 * (mental) sits Tue/Thu late afternoon; Phil (phil) sits Mon/Wed/Fri,
 * earlier in the afternoon; Phil 45-minute, Yannick 30-minute sessions — the times are
 * invented (there is no real production schedule to read yet), the PEOPLE
 * are not (SPECIALISTS, data/specialists.js). Session ids follow the real
 * seed convention (`YYYY-MM-DD-s<n>`, docs/portal/TEAM.md's "-x0 extras"
 * convention, new letter) purely for shape parity with live mode - nothing
 * here is ever written anywhere.
 *
 * `windowMonths` (contract v2.0 pin D, replaces the fixed
 * SPECIALIST_BOOKING_WINDOW_DAYS): the viewing athlete's own package window
 * via windowMonthsFor - defaults to one month (the no-package window) so
 * existing callers that still pass only (specialistId, today) (e.g.
 * SpecialistBooking.js's practice-mode branch) keep working.
 */
const PHIL_WEEKDAYS = new Set([1, 3, 5]); // Mon, Wed, Fri (Date#getUTCDay)
const MENTAL_WEEKDAYS = new Set([2, 4]); // Tue, Thu
const PHIL_TIMES = ['3:00 PM', '3:45 PM'];
// Sprint 20 (spec 6.1): Yannick's sessions are 30 minutes at 4:00 / 4:30 /
// 5:00 PM - the same three times seed-firestore.mjs writes.
const MENTAL_TIMES = ['4:00 PM', '4:30 PM', '5:00 PM'];

export function seedSpecialistDays(specialistId, today, windowMonths = 1) {
  const onMental = specialistId === 'mental';
  const weekdays = onMental ? MENTAL_WEEKDAYS : PHIL_WEEKDAYS;
  const times = onMental ? MENTAL_TIMES : PHIL_TIMES;
  // v1.7.1: capacity comes from the SPECIALISTS registry — phil is a group
  // session of 6, mental a true 1:1. A phil demo slot carries a believable
  // partial fill so the "spots left" treatment is reviewable in seed mode.
  const capacity = SPECIALISTS.find((s) => s.id === specialistId)?.capacity ?? 1;
  const durationMinutes = SPECIALISTS.find((s) => s.id === specialistId)?.durationMinutes ?? DEFAULT_DURATION_MINUTES;
  const toDate = openThrough(new Date(), windowMonths);

  const days = [];
  let i = 0;
  for (let date = today; date <= toDate; date = addDaysISO(date, 1), i++) {
    const dow = new Date(`${date}T00:00:00Z`).getUTCDay();
    // `dayIndex` is a fresh per-iteration binding (no-loop-func: the map()
    // callback below must not close over the outer, mutated `i` directly).
    const dayIndex = i;
    const slots = weekdays.has(dow)
      ? times.map((time, idx) => {
          const booked = capacity > 1 ? (dayIndex + idx) % capacity : 0;
          return {
            sessionId: `${date}-s${idx}`,
            time,
            booked,
            capacity,
            open: booked < capacity,
            durationMinutes,
          };
        })
      : [];
    days.push({ date, dayLabel: dayLabel(date, today), slots });
  }
  return days;
}

/**
 * Live payload for useSpecialistSlots — the same fetchSessionsInRange
 * useMonthSessions already reads (single-field 'date' range + orderBy, no
 * composite index), filtered down to this specialist's own session type and
 * grouped by date. Cancelled-by-academy sessions (sessions.status, Sprint 4
 * pin) are dropped like any other closure. EVERY day in the window is
 * emitted, even ones with no matching session at all - the day strip needs
 * a pill for every day, not just the ones with slots - which fetchSessions
 * InRange's result alone cannot guarantee (a day with zero specialist
 * sessions produces zero rows, not an empty-array placeholder).
 *
 * `windowMonths` (contract v2.0 pin D): openThrough(now, windowMonths)
 * replaces the fixed SPECIALIST_BOOKING_WINDOW_DAYS - the caller
 * (liveSpecialistSlots below) resolves it from the viewing athlete's own
 * package.
 *
 * Each day also carries `mark` ('tournament' | 'closed' | null, the booking
 * calendars' day marks) - judged on EVERY session the one read returned,
 * before the specialist filter throws the other types away. The read runs
 * MARKS_LOOKAHEAD_DAYS past the window for the marks' sync horizon only; the
 * days (and so the slots) still stop at toDate.
 */
async function liveSpecialistDays(specialistId, today, windowMonths) {
  const toDate = openThrough(new Date(), windowMonths);
  const sessions = await fetchSessionsInRange(today, addDaysISO(toDate, MARKS_LOOKAHEAD_DAYS));

  const byDate = new Map();
  for (const s of sessions) {
    // `bookable: false` is another family's Calendly appointment with
    // Yannick (functions/portal/calendly.js) - never a slot to offer.
    if (s.type !== specialistId || s.status === 'cancelled' || s.bookable === false) continue;
    const list = byDate.get(s.date);
    if (list) list.push(s);
    else byDate.set(s.date, [s]);
  }

  const days = [];
  for (let date = today; date <= toDate; date = addDaysISO(date, 1)) {
    // R2: a session that has started cannot be booked or waitlisted, so
    // today's earlier times are not offered at all.
    const onDate = (byDate.get(date) ?? [])
      .filter((s) => !sessionStarted(s))
      .sort((a, b) => (parseTimeToMinutes(a.time) ?? 0) - (parseTimeToMinutes(b.time) ?? 0));
    days.push({
      date,
      dayLabel: dayLabel(date, today),
      slots: onDate.map((s) => {
        const capacity = s.capacity ?? 1;
        const booked = s.booked ?? 0;
        return { sessionId: s.id, time: s.time, booked, capacity, open: booked < capacity, durationMinutes: s.durationMinutes ?? (SPECIALIST_BY_ID.get(specialistId)?.durationMinutes ?? DEFAULT_DURATION_MINUTES) };
      }),
    });
  }
  return markSpecialistDays(days, sessions);
}

/**
 * Specialist days -> the same days with `mark` set from `sessions` (every
 * type, through the lookahead) over the days' own span - see dayMarksFor.
 */
function markSpecialistDays(days, sessions) {
  if (!days.length) return days;
  const marks = dayMarksFor(sessions, days[0].date, days[days.length - 1].date);
  return days.map((d) => ({ ...d, mark: marks[d.date] ?? null }));
}

/**
 * The seed branch's marks: the generated season (which holds no specialist
 * sessions) plus this specialist's own seed slots, so a day with a slot is
 * never closed - the same invariant the live read keeps.
 */
function seedMarkedSpecialistDays(specialistId, days) {
  if (!days.length) return days;
  const from = days[0].date;
  const readEnd = addDaysISO(days[days.length - 1].date, MARKS_LOOKAHEAD_DAYS);
  const own = days.flatMap((d) => d.slots.map(() => ({ date: d.date, type: specialistId })));
  return markSpecialistDays(days, [...SEASON.filter((s) => s.date >= from && s.date <= readEnd), ...own]);
}

/**
 * The signed-in caller's own athleteId, or an explicit override (a parent's
 * chosen child) - shared by the days-window and tokens halves of
 * liveSpecialistSlots below so both agree on exactly the same athlete
 * without two separate identity resolutions. null when neither resolves (a
 * parent who has not chosen a child yet).
 */
async function resolveSpecialistAthleteId(athleteIdOverride) {
  if (athleteIdOverride) return athleteIdOverride;
  const profile = await fetchCurrentUser();
  return profile.athleteId ?? null;
}

/**
 * Live payload for useSpecialistSlots (contract v2.0, pin D/N — replaces
 * Sprint 11's per-type `entitlement` with the athlete's FULL token
 * position, same shape as useMembership's members[].tokens, and the fixed
 * SPECIALIST_BOOKING_WINDOW_DAYS with the resolved athlete's own package
 * window). `athleteId` is explicit rather than always "the signed-in user"
 * because a PARENT caller has no athlete of their own - the screen's child
 * selector supplies it once chosen (mirrors book(slot, {athleteId})'s own
 * optional-override shape elsewhere in this file).
 *
 * When no athlete resolves yet (a parent with no child chosen), the days
 * list still needs to render - MAX_WINDOW_MONTHS is the same safe superset
 * liveBooking uses for exactly this case, and `tokens` is null (same
 * "nothing to show before a pick" posture useBooking's own `tokens: null`
 * already has).
 */
async function liveSpecialistSlots(specialistId, athleteIdOverride, today) {
  const specialist = SPECIALIST_BY_ID.get(specialistId);
  const athleteId = await resolveSpecialistAthleteId(athleteIdOverride);
  if (!athleteId) {
    return {
      days: await liveSpecialistDays(specialistId, today, MAX_WINDOW_MONTHS),
      tokens: null, capReached: false,
      bookingMode: 'in-app', calendlyUrl: null, billingStatus: 'active',
      bookingOpen: bookingOpen(Date.now(), null), athlete: null, guardian: null, householdId: null,
    };
  }
  const athlete = await fetchAthlete(athleteId);
  const [pkg, bookings, household] = await Promise.all([
    athlete.packageId ? fetchPackage(athlete.packageId) : null,
    fetchBookings(athleteId, { householdId: athlete.householdId }),
    athlete.householdId ? fetchHousehold(athlete.householdId) : Promise.resolve(null),
  ]);
  const anchorDay = normalizeAnchorDay(household?.periodAnchorDay);
  const windowMonths = windowMonthsFor(pkg);
  const [rawDays, inputs] = await Promise.all([
    liveSpecialistDays(specialistId, today, windowMonths),
    fetchTokenInputs(athleteId, athlete.householdId, anchorDay, today),
  ]);
  // The sessions this athlete already waits for, each with its place when
  // the server says one: the sheet shows "On the waitlist", never Join again.
  const waiting = new Set((inputs.waitlist ?? []).map((w) => w.sessionId));
  const waitingHere = rawDays.flatMap((d) => d.slots).filter((s) => waiting.has(s.sessionId)).map((s) => s.sessionId);
  const positions = waitingHere.length ? await fetchWaitlistPositions(waitingHere) : {};
  // K04: the cadence is judged for each SLOT's month; the top-level
  // capReached keeps today's month for the summary line.
  const days = rawDays.map((d) => ({
    ...d,
    capReached: specialistId === 'mental' && coachingFor(bookings, today, pkg, d.date.slice(0, 7)).capReached,
    slots: d.slots.map((s) =>
      waiting.has(s.sessionId) ? { ...s, waitlisted: true, waitlistPosition: positionOf(positions, s.sessionId, athleteId) } : s
    ),
  }));
  // The position the booking check enforces (fetchTokenInputs).
  const tokens = withGraceReasons(tokensWithNextPeriod(pkg, bookings, anchorDay, today, inputs), inputs.graceTokens);
  const capReached = specialistId === 'mental' && coachingFor(bookings, today, pkg).capReached;
  // Sprint 20 (spec 6.1): Calendly only when the registry says so AND a URL
  // is configured; otherwise the in-app list, so seed/emulator keep working.
  const calendlyUrl = specialist?.bookingMode === 'calendly' ? calendlyUrlFor(pkg) : null;
  return {
    days, tokens, capReached,
    bookingMode: calendlyUrl ? 'calendly' : 'in-app',
    calendlyUrl,
    billingStatus: billingStatusOf(athlete),
    bookingOpen: bookingOpen(Date.now(), pkg),
    athlete: { id: athlete.id, name: athlete.name ?? null, loginEmail: athlete.loginEmail ?? null },
    guardian: { name: household?.guardian?.name ?? null, email: household?.guardian?.email ?? null },
    // D9: the link builder's utm_campaign (calendlyLinkFor, data/calendly.js).
    householdId: athlete?.householdId ?? null,
  };
}

/** Seed tokens for useSpecialistSlots - the same seed token derivation
 * useMembership's seed branch uses (seedMemberTokens, below), for one named
 * child (defaulting to the seed's one "signed-in athlete" fixture, jordan). */
function seedSpecialistTokens(specialistId, athleteId, today) {
  const child = seedChildById(athleteId || SEED_ATHLETE_ID);
  if (!child) return null;
  return seedMemberTokens(child, today);
}

/** K04 in seed too: judged for `monthISO` (a slot's month), default today's. */
function seedSpecialistCapReached(specialistId, athleteId, today, monthISO = today.slice(0, 7)) {
  const child = seedChildById(athleteId || SEED_ATHLETE_ID);
  return (
    specialistId === 'mental' &&
    Boolean(child) &&
    coachingFor(seedMemberBookingRows(child, today), today, packageById(child.packageId), monthISO).capReached
  );
}

/** Sprint 20: the seed child as the slot payload's `athlete` (no child logins in seed). */
function seedSpecialistAthlete(athleteId) {
  const c = seedChildById(athleteId || SEED_ATHLETE_ID);
  return c ? { id: c.id, name: c.name, loginEmail: null } : null;
}

/**
 * GET /specialists/:id/slots (Sprint 9 pin, contract v1.7; window and
 * entitlement rewritten Sprint 12, contract v2.0 pins D/N) — one
 * specialist's bookable slots over the athlete's own package window
 * (data/packages.js#windowMonthsFor via openThrough, replacing the fixed
 * SPECIALIST_BOOKING_WINDOW_DAYS), the Life-Time-style booking screen's day
 * strip + slot list. `specialistId` is 'phil' | 'mental' (SPECIALISTS' own
 * ids, which double as the sessions.type value). Booking a slot goes
 * through the EXISTING createBooking - unchanged signature, no booking
 * action lives on this hook.
 *
 * `entitlement` is retired in favour of `tokens` — the athlete's FULL token
 * position, same shape as useMembership's members[].tokens, or null before
 * a caller/athlete is resolvable — so SpecialistBooking's summary line
 * ("Uses 1 token · 7 left this period") and reserve-CTA gating read off the
 * SAME derivation the booking gate enforces. `athleteId` (optional
 * second-arg override) is the child a PARENT caller has picked; an athlete
 * caller ignores it (always their own).
 */
export function useSpecialistSlots(specialistId, { athleteId } = {}) {
  const live = isLive();
  const today = todayISO();
  // Post-write invalidation seam (Sprint 6 pin), both collections per the
  // Sprint 9 pin: a booking or a cancel always bumps 'bookings' AND
  // 'sessions' together (createBooking/cancelBooking, live.js) - subscribing
  // to both here (rather than 'sessions' alone, as useMonthSessions does)
  // re-runs this hook on either bump, not just the one that happens to fire
  // second. Sprint 11: 'athletes' too - a package assignment (pin B) changes
  // `tokens` without touching bookings or sessions at all.
  const sessionsGen = useInvalidation('sessions');
  const bookingsGen = useInvalidation('bookings');
  const athletesGen = useInvalidation('athletes');

  // No specialist picked yet (the picker stage) -> no query at all. Running
  // the live source with specialistId null returned honest-but-empty days,
  // which then sat as STALE data while the real fetch ran after a pick —
  // the screen's default-day effect read them and landed on today instead
  // of the first day with availability (integration browser pass).
  return useSeedResource(
    live && specialistId
      ? null
      : {
          days: specialistId
            ? seedMarkedSpecialistDays(specialistId, seedSpecialistDays(specialistId, today, windowMonthsFor(ATHLETE_PACKAGE))).map((d) => ({
                ...d,
                capReached: seedSpecialistCapReached(specialistId, athleteId, today, d.date.slice(0, 7)),
              }))
            : [],
          tokens: specialistId ? seedSpecialistTokens(specialistId, athleteId, today) : null,
          capReached: specialistId ? seedSpecialistCapReached(specialistId, athleteId, today) : false,
          // Sprint 20 (contract 4.3, D9): seed is always the in-app list (no
          // Calendly URL in a jest/harness env), paid, open, one seed child of
          // the one seed household; the same keys the live branch returns.
          bookingMode: 'in-app',
          calendlyUrl: null,
          billingStatus: 'active',
          bookingOpen: true,
          athlete: specialistId ? seedSpecialistAthlete(athleteId) : null,
          guardian: null,
          householdId: SEED_HOUSEHOLD_ID,
        },
    live && specialistId
      ? {
          source: () => liveSpecialistSlots(specialistId, athleteId, today),
          deps: ['specialist-slots', specialistId, athleteId, today, sessionsGen, bookingsGen, athletesGen],
        }
      : undefined
  );
}

/**
 * Seed branch for useSpecialistSessions — the same pattern
 * seedSpecialistDays generates, flattened to the specialist's own day-view
 * shape. Roster names stay EMPTY in seed mode (no invented people; the
 * screen renders the booked count and, live, the real names). This is a
 * STAFF operational view, not a member booking surface - it is not any one
 * athlete's own window (contract v2.0 pin D retired the flat
 * SPECIALIST_BOOKING_WINDOW_DAYS this used to read), so it uses
 * MAX_WINDOW_MONTHS, the widest window any package offers, ensuring Phil/
 * Yannick always see everything any athlete could possibly have booked.
 */
function seedSpecialistDaySessions(specialistId, today) {
  return seedSpecialistDays(specialistId, today, MAX_WINDOW_MONTHS)
    .flatMap((d) =>
      d.slots.map((s) => ({
        sessionId: s.sessionId,
        date: d.date,
        dayLabel: d.dayLabel,
        time: s.time,
        booked: s.booked,
        capacity: s.capacity,
        // Sprint 20 (spec 6.1): the attendance `block` carries the length;
        // `type` is the specialist id, which doubles as sessions.type.
        type: specialistId,
        durationMinutes: s.durationMinutes,
        athletes: [],
      }))
    );
}

/**
 * Live payload for useSpecialistSessions — the specialist's OWN upcoming
 * sessions, each joined to its live roster (non-cancelled bookings ->
 * athlete names via the per-id join; a name the caller cannot read renders
 * as a count, not a crash). Powers the "My sessions" day view (Sprint 9
 * amendment v1.7.1: specialist-side access). MAX_WINDOW_MONTHS, not any one
 * athlete's own window - see seedSpecialistDaySessions' comment above.
 */
async function liveSpecialistSessions(specialistId, today) {
  const toDate = openThrough(new Date(), MAX_WINDOW_MONTHS);
  const sessions = await fetchSessionsInRange(today, toDate);
  const mine = sessions
    .filter((s) => s.type === specialistId && s.status !== 'cancelled')
    .sort(byDateThenId);

  const withRosters = await Promise.all(
    mine.map(async (s) => {
      const bookings = await fetchBookingsBySession(s.id);
      const active = bookings.filter((b) => b.status !== 'cancelled');
      const athletes = await fetchAthletesByIds(active.map((b) => b.athleteId));
      const nameById = new Map(athletes.map((a) => [a.id, a.name]));
      return {
        sessionId: s.id,
        date: s.date,
        dayLabel: dayLabel(s.date, today),
        time: s.time,
        booked: active.length,
        capacity: s.capacity ?? 1,
        type: s.type,
        durationMinutes: s.durationMinutes ?? (SPECIALIST_BY_ID.get(s.type)?.durationMinutes ?? DEFAULT_DURATION_MINUTES),
        athletes: active.map((b) => ({
          athleteId: b.athleteId,
          name: nameById.get(b.athleteId) ?? null,
          // Who is coming (owner ruling, 2026-09-22). The booking is still
          // FOR the child - absent means they attend, which is every booking
          // written before the choice existed.
          attendee: b.attendee ?? 'athlete',
        })),
      };
    })
  );
  return { sessions: withRosters };
}

/**
 * GET /specialists/:id/my-sessions (Sprint 9 amendment v1.7.1) — the
 * signed-in specialist's upcoming sessions with per-session rosters:
 * { data: { sessions: [{ sessionId, date, dayLabel, time, booked,
 *   capacity, athletes: [{ athleteId, name }] }] }, loading, error }.
 * Subscribed to both invalidation generations, so a family's booking or
 * cancellation refreshes an open specialist day view immediately.
 */
export function useSpecialistSessions(specialistId) {
  const live = isLive();
  const today = todayISO();
  const sessionsGen = useInvalidation('sessions');
  const bookingsGen = useInvalidation('bookings');

  return useSeedResource(
    live && specialistId ? null : { sessions: specialistId ? seedSpecialistDaySessions(specialistId, today) : [] },
    live && specialistId
      ? {
          source: () => liveSpecialistSessions(specialistId, today),
          deps: ['specialist-sessions', specialistId, today, sessionsGen, bookingsGen],
        }
      : undefined
  );
}

/**
 * '2026-11-02' -> 'Mon' — the short weekday the household card's compact
 * `next.when` line needs ("Mon 4:00 PM", matching the seed shape). The only
 * place this abbreviation is needed; displaySession's dayLabel (the long
 * form, "Today" / "Monday, Nov 2") is what every other surface reads.
 */
function shortWeekday(iso) {
  return new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone: 'UTC' }).format(
    new Date(`${iso}T00:00:00Z`)
  );
}

/**
 * One household athlete's full home-card: package + token position (same
 * derivation as useHouseholdAthletes), their real next upcoming booking, and
 * a contract standing/percentage built the same way liveContract() builds
 * the full Contract screen (below) — so a child's card can never disagree
 * with their own Contract screen. Sprint 6 pin: "useHousehold's per-child
 * cards... real athletes + real allowances", read as covering every field a
 * card shows, not just the token position — "no screen may show seed
 * numbers in live mode."
 *
 * `standing` is null (no badge) rather than the seed's dashed 'New' state
 * when there is no reliable "just enrolled" signal to read live (no
 * enrollment-date field on the athlete doc) — an unbadged card, not an
 * invented one. Same for `age`/`ageLine`: dob is frequently null (per
 * DATA-MODEL.md, "no birthday is invented"), and a null dob renders no age
 * rather than a fabricated one. `anchorDay` (contract v2.0 pin B) is the
 * household's periodAnchorDay, resolved once by the caller (liveHousehold)
 * rather than re-fetched per child.
 */
async function liveChildCard(a, today, anchorDay) {
  // Sprint 20 (load time): the four reads that depend only on the athlete doc
  // go out together - one round trip instead of four in a row per child.
  // Parent context: the compound filter is what makes the bookings read
  // provable under the rules (see fetchBookings).
  // The owner's rule (2026-10-28): every child is on the contract, judged
  // day by day against contractMinutesFor - no stored tier gates the card.
  const loginEmail = a.loginEmail ?? null;
  const [pkg, bookings, logs, invite] = await Promise.all([
    a.packageId ? fetchPackage(a.packageId) : Promise.resolve(null),
    fetchBookings(a.id, { householdId: a.householdId }),
    fetchContractLogs(a.id),
    loginEmail ? fetchLoginInvite(loginEmail) : Promise.resolve(null),
  ]);
  const active = bookings.filter((b) => b.status !== 'cancelled');
  const upcoming = active.filter((b) => b.date >= today).sort(byDateThenId);

  let next = null;
  if (upcoming.length) {
    const sessionsById = new Map(
      (await fetchSessionsByIds([upcoming[0].sessionId])).map((s) => [s.id, s])
    );
    const s = sessionsById.get(upcoming[0].sessionId);
    if (s) {
      const disp = displaySession(s, today);
      next = { type: disp.type, when: `${shortWeekday(s.date)} ${disp.time} ${disp.meridiem}`, meta: disp.name };
    }
  }

  const minutesByDate = new Map(logs.map((l) => [l.date, l.minutes || 0]));
  const m = buildContractMonthFromLogs({
    today, minutesByDate, contractMinutes: (iso) => contractMinutesFor(a, iso), ...contractWindowFor(a),
  });
  // Nothing due yet (before the start, or the month's first weekday) is
  // "—", not a grey 0%; no badge at all outside the contract window.
  const contract = m.dueSoFar ? Math.round((m.logged / m.dueSoFar) * 100) : null;
  const standing = m.behind
    ? { tone: 'yellow', label: 'Behind' }
    : m.notStarted || m.ended
    ? null
    : { tone: 'green', label: 'On track' };

  const age = ageFromDob(a.dob ?? null);
  // Sprint 20 (spec 3.2): the child's own login for the parent home's
  // ChildCard line. A parent may read their own household's invites (rules,
  // Task 5); no loginEmail == the parent's account runs the child.
  const login = loginStateFor(loginEmail, invite);
  // Composed once here (review 2026-09-30): the contract part is dropped at
  // the source while the contract is hidden, so no screen scrubs it.
  const ageLine = hideContractParts(
    [age != null ? `Age ${age}` : null, `${contractMinutesFor(a, today)} min a day`]
      .filter(Boolean)
      .join(' · ') || null
  );

  return {
    id: a.id,
    name: a.name,
    age,
    ageLine,
    standing,
    next,
    contract,
    packageId: a.packageId ?? null,
    // Sprint 20 (spec 4.4): the parent home banner and Pay button key off this; absent == active.
    billingStatus: billingStatusOf(a),
    // 2026-10-02: the handicap on file (null == none), off the doc in hand.
    handicap: handicapOf(a),
    loginEmail,
    login,
    tokens: await homeTokens(a, pkg, bookings, anchorDay, today),
  };
}

/**
 * Live payload for useHousehold — the household's real name, every real
 * child's real card (liveChildCard above), and a billing placeholder: Stripe
 * wiring is out of scope this sprint (TEAM.md, Sprint 5 "Billing rows"), so
 * `status: 'ok'` here is the same documented placeholder useBillingSummary's
 * `status: 'active'` already uses, not a fabricated payment state.
 */
async function liveHousehold(today) {
  const profile = await fetchCurrentUser();
  if (!profile.householdId) {
    throw new LiveDataError(
      ERR.INVALID,
      `users/${profile.uid} has no householdId - this surface is wired for ` +
        'parent accounts only.'
    );
  }
  const [household, athletes] = await Promise.all([
    fetchHousehold(profile.householdId),
    fetchHouseholdAthletes(profile.householdId),
  ]);
  const anchorDay = normalizeAnchorDay(household.periodAnchorDay);
  const children = await Promise.all(athletes.map((a) => liveChildCard(a, today, anchorDay)));
  return {
    name: household.name ?? null,
    date: longDayLabel(today),
    children,
    billing: { status: 'ok', retryStep: 0 },
  };
}

/** GET /athletes?guardian=:id + GET /billing/:householdId (08). `practice`
 * pins the seed (the walkthrough's family step shows the Whitfields). */
export function useHousehold({ variant = 'three', practice = false } = {}) {
  const live = !practice && isLive();
  const today = todayISO();
  // Post-write invalidation seam (Sprint 6 pin): a booking or a contract log
  // can change a child's card - re-run after either bumps. Sprint 10:
  // 'athletes' too - the contract's one-time stamp (ensureContractStart,
  // 2026-10-28) writes contractStart, which opens the child's window.
  const bookingsGen = useInvalidation('bookings');
  const contractLogsGen = useInvalidation('contractLogs');
  const athletesGen = useInvalidation('athletes');

  const demo = demoOpts(variant, "Your family's data didn't load.");
  const children = (variant === 'one' ? HOUSEHOLD.children.slice(0, 1) : HOUSEHOLD.children).map((c) => ({
    ...c,
    ageLine: hideContractParts(c.ageLine),
    billingStatus: c.billingStatus ?? 'active',
    handicap: handicapOf(c),
    // Sprint 20 (spec 3.2): the seed family has no child logins - state 'none'.
    loginEmail: null,
    login: { state: 'none', claimedAt: null },
  }));
  const billing = variant === 'payment' ? BILLING_ISSUE : HOUSEHOLD.billing;

  return useSeedResource(
    demo || live ? null : { ...HOUSEHOLD, date: TODAY, children, billing },
    demo ??
      (live
        ? {
            source: () => liveHousehold(today),
            deps: ['household', today, bookingsGen, contractLogsGen, athletesGen],
          }
        : undefined)
  );
}

/**
 * Live payload for useHouseholdAthletes - every athlete in the household,
 * joined to their package for packageName + token grant, usage derived from
 * bookings the same way liveSchedule/liveBooking do (no stored counter to
 * drift).
 */
async function liveHouseholdAthletes(today) {
  const profile = await fetchCurrentUser();
  if (!profile.householdId) {
    throw new LiveDataError(
      ERR.INVALID,
      `users/${profile.uid} has no householdId - this surface is wired for ` +
        'parent accounts only.'
    );
  }
  const [household, athletes] = await Promise.all([
    fetchHousehold(profile.householdId),
    fetchHouseholdAthletes(profile.householdId),
  ]);
  const anchorDay = normalizeAnchorDay(household.periodAnchorDay);
  return Promise.all(
    athletes.map(async (a) => {
      const [pkg, bookings, inputs] = await Promise.all([
        a.packageId ? fetchPackage(a.packageId) : null,
        // Parent context — compound filter for rules provability (fetchBookings).
        fetchBookings(a.id, { householdId: profile.householdId }),
        // Waitlist holds and bonus tokens, as the booking check counts them.
        fetchTokenInputs(a.id, profile.householdId, anchorDay, today),
      ]);
      return {
        id: a.id,
        name: a.name,
        packageId: a.packageId ?? null,
        packageName: pkg ? pkg.name : null,
        billingStatus: billingStatusOf(a),
        // 2026-10-02: Book a Session judges each child against a reserved
        // block on this (data/reserved.js); null == none on file.
        handicap: handicapOf(a),
        // With the next period's count (2026-11-01): a parent's Book a Session
        // reads this row, and judges a next-period session by that period.
        tokens: withGraceReasons(
          tokensWithNextPeriod(pkg, bookings, anchorDay, today, { ...inputs, billingStatus: billingStatusOf(a) }),
          inputs.graceTokens
        ),
      };
    })
  );
}

/**
 * GET /athletes?householdId=:id (Sprint 5) - every athlete in the signed-in
 * parent's household, replacing the hard-coded Whitfield seed on surfaces
 * that need the real roster (not the fixed-shape dashboard cards
 * useHousehold serves). Live: athletes where householdId == the caller's
 * householdId - the equality filter firestore.rules can prove on a list read.
 */
export function useHouseholdAthletes() {
  const live = isLive();
  const today = todayISO();
  // Post-write invalidation seam (Sprint 6 pin): a new booking changes a
  // child's token position - re-run after any bookings write, including one
  // made through useBooking's book() for this same child. Sprint 10:
  // 'athletes' too, so a parent's tier pick on AthleteDetail (contract
  // v1.8, B) is reflected here without a remount.
  const bookingsGen = useInvalidation('bookings');
  const athletesGen = useInvalidation('athletes');
  const seedRows = HOUSEHOLD.children.map((c) => ({
    id: c.id,
    name: c.name,
    packageId: c.packageId,
    packageName: packageById(c.packageId)?.name ?? null,
    billingStatus: c.billingStatus ?? 'active',
    handicap: handicapOf(c),
    tokens: c.tokens,
  }));
  return useSeedResource(
    live ? null : seedRows,
    live
      ? { source: () => liveHouseholdAthletes(today), deps: ['household-athletes', bookingsGen, athletesGen] }
      : undefined
  );
}

/**
 * GET /billing/:householdId (Sprint 5) - one row per child: package name and
 * price from the STATIC catalogue (data/packages.js), never from a Firestore
 * package doc, which by policy carries no price. Status is an 'active'
 * placeholder until Stripe wiring lands.
 */
export function useBillingSummary() {
  const live = isLive();
  const rowFor = (id, name, packageId) => {
    const pkg = packageById(packageId);
    return {
      athleteId: id,
      name,
      packageName: pkg ? pkg.name : null,
      price: pkg ? pkg.price : null,
      status: 'active',
    };
  };
  const seedRows = HOUSEHOLD.children.map((c) => rowFor(c.id, c.name, c.packageId));

  const liveBillingSummary = async () => {
    const profile = await fetchCurrentUser();
    if (!profile.householdId) {
      throw new LiveDataError(
        ERR.INVALID,
        `users/${profile.uid} has no householdId - billing is a parent surface only.`
      );
    }
    const athletes = await fetchHouseholdAthletes(profile.householdId);
    return { rows: athletes.map((a) => rowFor(a.id, a.name, a.packageId)) };
  };

  return useSeedResource(
    live ? null : { rows: seedRows },
    live ? { source: liveBillingSummary, deps: ['billing-summary'] } : undefined
  );
}

/* ------------------------------------------------------------------------- *
 * Membership & tokens (Sprint 11, contract v1.9; REWRITTEN Sprint 12,
 * contract v2.0 pin N — the two-pool golf/fitness entitlement split is
 * retired for ONE package + ONE token position). "Billing" stays off the
 * live member surface (owner ruling, unchanged). Both data modes route
 * every number through data/packages.js#tokensFor via this file's own
 * deriveTokens/tokensWithNextPeriod, the SAME derivation
 * live.js#assertWithinPeriodCap reads, so Membership, SpecialistBooking's
 * summary and the booking gate itself can never disagree.
 * ------------------------------------------------------------------------- */

/** One seed HOUSEHOLD child by id, or null - shared by the membership and
 * specialist-tokens seed branches below. */
function seedChildById(athleteId) {
  return HOUSEHOLD.children.find((c) => c.id === athleteId) ?? null;
}

/**
 * The contract requirement per seed child under the owner's rule
 * (2026-10-28: 20 min a day, 10 under 12) - contractMinutesFor on the seed
 * dobs: Jordan 14 and Reese 12 are 20, Nico 9 is 10.
 */
const SEED_CONTRACT_MINUTES = { jordan: 20, reese: 20, nico: 10 };

/**
 * Believable non-zero token usage for the seed demo, this period - mirrors
 * each child's pre-existing two-pool "used" total (jordan 3+1, reese 2+2,
 * nico 0+0) collapsed into the one pool tokens now spends from. Nothing
 * invented beyond reshaping numbers already in this file.
 */
const SEED_TOKENS_USED = { jordan: 4, reese: 4, nico: 0 };

/**
 * Booking-shaped rows for one seed child's CURRENT period, built to
 * reproduce SEED_TOKENS_USED - so tokensFor run over these rows lands on a
 * believable, non-invented position. Dates are `today` unconditionally
 * (never a fixed seed date) so these always land in the live period
 * whenever the demo is viewed.
 */
function seedMemberBookingRows(child, today) {
  const used = SEED_TOKENS_USED[child.id] ?? 0;
  const periodKey = positionPeriodFor(today, PERIOD_ANCHOR_DAY).periodKey;
  const rows = [];
  for (let i = 0; i < used; i++) rows.push({ periodKey, status: 'confirmed', date: today });
  return rows;
}

/**
 * Grace tokens for one seed child (contract v2.1, Sprint 13) - GRACE_TOKEN
 * (data/seed.js) is reese's alone; every other child sees none, matching
 * the harness's one-grace-token story exactly.
 */
function seedMemberGraceTokens(child) {
  return child.id === GRACE_TOKEN.athleteId ? [GRACE_TOKEN] : [];
}

/** tokensWithNextPeriod, run for one seed child - shared by useMembership's
 * seed branch and useSpecialistSlots' seed tokens above. */
function seedMemberTokens(child, today) {
  const pkg = packageById(child.packageId);
  const graceTokens = seedMemberGraceTokens(child);
  return withGraceReasons(
    tokensWithNextPeriod(pkg, seedMemberBookingRows(child, today), PERIOD_ANCHOR_DAY, today, { graceTokens }),
    graceTokens
  );
}

/** One useMembership member row for a seed child - same field set the live
 * branch (liveMemberEntry below) produces. */
function seedMemberEntry(child, today) {
  const pkg = packageById(child.packageId);
  return {
    athleteId: child.id,
    name: child.name,
    dob: child.dob ?? null,
    billingStatus: child.billingStatus ?? 'active',
    handicap: handicapOf(child),
    package: pkg
      ? { id: pkg.id, name: pkg.name, price: pkg.price ?? null, pending: pkg.pending ?? false, tokens: pkg.tokens ?? null, kind: pkg.kind ?? null }
      : null,
    tokens: seedMemberTokens(child, today),
    coaching: coachingFor(seedMemberBookingRows(child, today), today, pkg),
    contractMinutes: SEED_CONTRACT_MINUTES[child.id] ?? contractMinutesFor(child, today),
    periodEnd: periodFor(today, PERIOD_ANCHOR_DAY).periodEnd,
  };
}

/**
 * One useMembership member row, live mode - the athlete's ONE package's
 * facts (never a Firestore price, by the same no-dollar-amounts policy
 * useBillingSummary already follows) plus the full token position off the
 * athlete's real bookings this period.
 */
async function liveMemberEntry(a, today, anchorDay) {
  // The grant read is the first (prepaid) period's before the season.
  const { periodKey, periodEnd } = positionPeriodFor(today, anchorDay);
  const [pkg, bookings, graceTokens, waitlistEntries, tokenPeriod, nextTokenPeriod] = await Promise.all([
    a.packageId ? fetchPackage(a.packageId) : null,
    fetchBookings(a.id, { householdId: a.householdId }),
    fetchGraceTokensByAthlete(a.id),
    // The household filter is what a parent's read is provable on (waitlist.js).
    fetchWaitlistByAthlete(a.id, { householdId: a.householdId }),
    // Contract v2.1, pin C: read BY ID for the current period only - "no
    // query, no index". Absent (no doc issued yet) resolves to null, which
    // tokensFor treats as "the package's own grant" (pin C's own words).
    fetchTokenPeriod(a.id, periodKey),
    // And the next period's, when one was issued (2026-11-01, see fetchTokenInputs).
    fetchTokenPeriod(a.id, periodFor(addDaysISO(periodEnd, 1), anchorDay).periodKey),
  ]);
  // Price comes from the STATIC catalogue (packageById), never the Firestore
  // doc's own `price` field - policy: no dollar amounts in Firestore (same
  // reasoning DATA-MODEL.md gives for useBillingSummary's rows). Verified
  // live: a Firestore package doc's `price` reads back null in this
  // emulator, same as production will until a deploy-time price import.
  return {
    athleteId: a.id,
    name: a.name,
    // The athlete's own Book a Session reads it: who may buy a session
    // token there (data/singleToken.js ownLoginMayBuy). null when not on file.
    dob: a.dob ?? null,
    billingStatus: billingStatusOf(a),
    // Book a Session's out-of-tokens banner offers an extra session at the
    // member rate only on a live, paid subscription (singleToken.js
    // extraSessionOffer): status, plus oneTime / subscribed when set.
    billing: tierBillingOf(a),
    // 2026-10-02: the athlete's own Book a Session judges a reserved block
    // on this (data/reserved.js); null == none on file.
    handicap: handicapOf(a),
    package: pkg
      ? {
          id: pkg.id,
          name: pkg.name,
          price: packageById(pkg.id)?.price ?? null,
          // Seeds strip price and pending; both are catalogue facts, read from it.
          pending: packageById(pkg.id)?.pending ?? false,
          tokens: pkg.tokens ?? null,
          kind: pkg.kind ?? null,
        }
      : null,
    // Contract v2.1: grace tokens, waitlist reservations and an ops/Stripe
    // issuance (when one exists) all now feed the position - see
    // tokensWithNextPeriod/deriveTokens' own comments. withGraceReasons adds
    // the `reason` field the hook seam wants that tokensFor's own output
    // does not carry.
    tokens: withGraceReasons(
      tokensWithNextPeriod(pkg, bookings, anchorDay, today, { graceTokens, waitlist: waitlistEntries, tokenPeriod, nextTokenPeriod }),
      graceTokens
    ),
    coaching: coachingFor(bookings, today, pkg),
    // The requirement today (owner's rule, 2026-10-28), never the stored tier.
    contractMinutes: contractMinutesFor(a, today),
    periodEnd: periodFor(today, anchorDay).periodEnd,
  };
}

/**
 * Live payload for useMembership - `household: null` and a single self-entry
 * for an athlete-linked account; the real household (with its
 * periodAnchorDay) plus every member in fetchHouseholdAthletes' own order
 * for a parent. Whichever role, screens cannot tell (the pinned shape is
 * identical either way).
 */
async function liveMembership(today) {
  const profile = await fetchCurrentUser();
  if (profile.athleteId) {
    const athlete = await fetchAthlete(profile.athleteId);
    const household = athlete.householdId ? await fetchHousehold(athlete.householdId) : null;
    const anchorDay = normalizeAnchorDay(household?.periodAnchorDay);
    return { household: null, members: [await liveMemberEntry(athlete, today, anchorDay)] };
  }
  if (profile.householdId) {
    const [household, athletes] = await Promise.all([
      fetchHousehold(profile.householdId),
      fetchHouseholdAthletes(profile.householdId),
    ]);
    const anchorDay = normalizeAnchorDay(household.periodAnchorDay);
    const members = await Promise.all(athletes.map((a) => liveMemberEntry(a, today, anchorDay)));
    return {
      household: {
        id: household.id,
        name: household.name ?? null,
        periodAnchorDay: anchorDay,
        // Contract v2.1, pin H: absent == active (households.membership is
        // Admin-SDK/Stripe-handler-only - never written by this app's own
        // client code). `status`/`currentPeriodEnd` only - stripeSubscriptionStatus/
        // lastEventId/updatedAt are Stripe-handler bookkeeping the UI never
        // needs to render.
        membership: household.membership
          ? { status: household.membership.status, currentPeriodEnd: household.membership.currentPeriodEnd ?? null }
          : null,
        stripeCustomerId: household.stripeCustomerId ?? null,
      },
      members,
    };
  }
  throw new LiveDataError(
    ERR.INVALID,
    `users/${profile.uid} has neither athleteId nor householdId - membership is wired for ` +
      'athlete-linked or parent accounts only.'
  );
}

/**
 * GET /membership (Sprint 11 pin D, contract v1.9; payload rewritten
 * Sprint 12, contract v2.0 pin N) - the parked Billing surface's
 * replacement: what each athlete's ONE package ENTITLES them to, never what
 * is owed. `{ data: { household: {id,name,periodAnchorDay}|null, members:
 * [{ athleteId, name, dob, package, tokens, contractMinutes, periodEnd }] },
 * loading, error }`. Parent: every household athlete, household order.
 * Athlete: self only, household null.
 */
export function useMembership() {
  const live = isLive();
  const today = todayISO();
  const bookingsGen = useInvalidation('bookings');
  const athletesGen = useInvalidation('athletes');

  const seedMembers = HOUSEHOLD.children.map((c) => seedMemberEntry(c, today));

  return useSeedResource(
    live
      ? null
      : {
          household: {
            id: 'whitfield',
            name: HOUSEHOLD.name,
            periodAnchorDay: PERIOD_ANCHOR_DAY,
            // Whitfield is active in the live emulator seed too (absent ==
            // active) - the harness's past_due demo state is
            // PAST_DUE_MEMBERSHIP (data/seed.js), for whichever screen wires
            // a variant to it, not the default membership payload.
            membership: null,
            stripeCustomerId: null,
          },
          members: seedMembers,
        },
    live
      ? { source: () => liveMembership(today), deps: ['membership', today, bookingsGen, athletesGen] }
      : undefined
  );
}

/**
 * `assign(athleteId, { packageId })` (Sprint 11 pin B, contract v1.9;
 * narrowed Sprint 12, contract v2.0 pin A/B - one package, not a golf/
 * fitness pair) - ops/owner only, enforced by firestore.rules'
 * packageAssignmentUpdateOk(), not here; live.js's setAthletePackages does
 * the one-shot updateDoc + bump('athletes'). `saving`/`error` are hook-owned
 * state (AthleteDetail's membership editor reads them directly for its Save
 * button, rather than each caller re-deriving the same try/finally). Seed
 * mode is a local echo, same discipline as useNotificationPrefs.
 */
export function useAssignPackages() {
  const live = isLive();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const assign = async (athleteId, { packageId, facilityAccess }) => {
    setSaving(true);
    setError(null);
    try {
      if (!live) {
        return { athleteId, packageId, facilityAccess: facilityAccess ?? null, simulated: true };
      }
      return await setAthletePackages(athleteId, { packageId, facilityAccess });
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  return { assign, saving, error };
}

/**
 * `setPeriodAnchorDay(day)` (contract v2.0 pin B/N, new) - ops/owner only,
 * enforced by firestore.rules' householdPeriodUpdateOk(), not here.
 * live.js's setHouseholdPeriodAnchorDay does the one-shot updateDoc +
 * bump('households'). The hook is scoped to ONE household (`householdId`,
 * the family the caller is currently editing on the Membership screen) so
 * the returned setter takes only the new day, matching AthleteDetail's
 * existing useAssignPackages() call shape. Seed mode is a local echo, same
 * discipline as useAssignPackages.
 */
export function useHouseholdSettings(householdId) {
  const live = isLive();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const setPeriodAnchorDay = async (day) => {
    setSaving(true);
    setError(null);
    try {
      if (!live || !householdId) {
        return { householdId, periodAnchorDay: normalizeAnchorDay(day), simulated: true };
      }
      return await setHouseholdPeriodAnchorDay(householdId, day);
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  /**
   * `setStripeIds({ stripeCustomerId, stripeSubscriptionId })` (contract
   * v2.1, pin H, new) - ops/owner only, enforced by firestore.rules'
   * householdSettingsUpdateOk(), not here. Same saving/error/seed-echo
   * discipline as setPeriodAnchorDay just above.
   */
  const setStripeIds = async ({ stripeCustomerId = null, stripeSubscriptionId = null } = {}) => {
    setSaving(true);
    setError(null);
    try {
      if (!live || !householdId) {
        return { householdId, stripeCustomerId, stripeSubscriptionId, simulated: true };
      }
      return await setHouseholdStripeIds(householdId, { stripeCustomerId, stripeSubscriptionId });
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  return { setPeriodAnchorDay, setStripeIds, saving, error };
}

/**
 * One reservation row - the SAME shape useSchedule's own resolve() produces
 * (displaySession fields + bookingId/status/cancellable), plus the fields
 * Sprint 11 pin F adds (athleteId, instructor, durationMinutes) and Sprint
 * 12 pin N adds (periodKey, nextPeriod). Shared by the live and seed
 * branches below so the two can never diverge in shape.
 *
 * `instructor`: the specialist's own name for phil/mental
 * (data/specialists.js - academy-public, no extra read). For training/
 * tournament this needs the session's assigned coachId resolved to a
 * display name, and there is currently NO rules-compliant read for a parent
 * to resolve an arbitrary coach's users doc - the users collection's read
 * rule is self/ops/owner only (firestore.rules), and widening it to every
 * signed-in caller is a real privacy tradeoff for a minors' app, not this
 * lane's unilateral call. `instructor` resolves to null for training/
 * tournament in LIVE mode until the PM decides between a denormalized
 * `sessions.coachName` snapshot (the tournamentResults `name` precedent,
 * v1.5.1) or a narrow rules widening - flagged in the routing report, not
 * silently invented. Seed mode uses the seed's own named coach fixture
 * (COACH.name, 'Luke' - already established elsewhere, not a new invention)
 * since seed data has no rules boundary to respect.
 *
 * `durationMinutes`: 60 for a generator block, 45 for any specialist slot
 * (data/specialists.js's seeded 45-minute slots) - the pin's own numbers.
 *
 * `periodKey`/`nextPeriod`: computed from the SESSION's own date against
 * the household's anchorDay, same convention as liveSchedule/useSchedule's
 * resolve() above - a display read never needs to trust a possibly-missing
 * stored field.
 */
function reservationRow(s, b, today, instructor, anchorDay, currentPeriodKey) {
  const specialist = SPECIALIST_BY_ID.get(s.type);
  const periodKey = periodFor(s.date, anchorDay).periodKey;
  const source = b.source ?? 'portal';
  return {
    ...displaySession(s, today),
    badge:
      b.status === 'confirmed'
        ? { tone: 'green', label: 'Confirmed' }
        : b.status === 'attended'
        ? { tone: 'neutral', label: 'Attended' }
        : b.status === 'noshow'
        ? { tone: 'red', label: 'No-show' }
        : b.status === 'cancelled'
        ? { tone: 'red', label: 'Cancelled' }
        : null,
    bookingId: b.bookingId,
    status: b.status,
    cancellable: b.status === 'confirmed' && s.date > today && source !== 'calendly',
    singleToken: isSingleTokenId(b.graceTokenId),
    // Contract v2.1, pin G: present on a cancelled row only.
    cancelReason: b.cancelReason ?? null,
    cancelledBy: b.cancelledBy ?? null,
    athleteId: b.athleteId,
    source,
    // Contract v2.1 (Sprint 12 pin): who the family said would walk in.
    // Only a Yannick 1:1 can carry 'parent'; everything else is the athlete.
    attendee: b.attendee ?? 'athlete',
    instructor: instructor ?? (specialist ? specialist.name : null),
    durationMinutes: s.durationMinutes ?? (specialist ? specialist.durationMinutes : DEFAULT_DURATION_MINUTES),
    periodKey,
    nextPeriod: periodKey > currentPeriodKey,
  };
}

/**
 * Live payload for useHouseholdReservations - ONE query on (householdId,
 * date) [DATA-MODEL.md index 3, fetchHouseholdBookings], joined to sessions
 * for display facts, grouped per household member into upcoming/past.
 */
async function liveHouseholdReservations(today) {
  const profile = await fetchCurrentUser();
  if (!profile.householdId) {
    throw new LiveDataError(
      ERR.INVALID,
      `users/${profile.uid} has no householdId - Reservations is a parent surface only.`
    );
  }
  const [household, athletes, bookings, waitlistEntries] = await Promise.all([
    fetchHousehold(profile.householdId),
    fetchHouseholdAthletes(profile.householdId),
    fetchHouseholdBookings(profile.householdId),
    fetchWaitlistByHousehold(profile.householdId),
  ]);
  const anchorDay = normalizeAnchorDay(household.periodAnchorDay);
  const currentPeriodKey = positionPeriodFor(today, anchorDay).periodKey; // the meter's period, as liveSchedule
  // Contract v2.1, pin G: cancelled bookings are no longer dropped - every
  // status renders, with cancelReason/cancelledBy on the cancelled ones.
  const sessionsById = new Map(
    (
      await fetchSessionsByIds([
        ...bookings.map((b) => b.sessionId),
        ...waitlistEntries.map((w) => w.sessionId),
      ])
    ).map((s) => [s.id, s])
  );

  // `unlimited`: an Elite athlete's rows carry no token wording (Reservations.js).
  const byAthlete = new Map(
    athletes.map((a) => [a.id, { athleteId: a.id, name: a.name, unlimited: packageById(a.packageId)?.tokens === null, upcoming: [], past: [] }])
  );
  for (const b of bookings) {
    const s = sessionsById.get(b.sessionId);
    const bucket = byAthlete.get(b.athleteId);
    if (!s || !bucket) continue; // dropped, same null-resolve rule as useSchedule/liveSchedule above
    const row = reservationRow(
      s,
      { bookingId: b.id, status: b.status, athleteId: b.athleteId, cancelReason: b.cancelReason, cancelledBy: b.cancelledBy, source: b.source, graceTokenId: b.graceTokenId },
      today,
      null,
      anchorDay,
      currentPeriodKey
    );
    (s.date >= today ? bucket.upcoming : bucket.past).push(row);
  }
  // Contract v2.1, pin F: every member's waitlisted rows, one shared
  // fetchWaitlistByHousehold query rather than per-athlete round trips.
  const waitlistRows = await resolveWaitlistRows(waitlistEntries, sessionsById, today, anchorDay, currentPeriodKey);
  for (const row of waitlistRows) {
    const bucket = byAthlete.get(row.athleteId);
    if (bucket) bucket.upcoming.push(row); // a waitlist entry is always for a future session
  }
  const members = athletes.map((a) => byAthlete.get(a.id));
  for (const m of members) {
    m.upcoming.sort(byDateThenId);
    m.past.sort((x, y) => -byDateThenId(x, y));
  }
  return { members };
}

/** Seed member row for one household child - jordan (SEED_ATHLETE_ID) reuses
 * the SAME BOOKED_UPCOMING/BOOKED_PAST references useSchedule's own seed
 * branch resolves (never a second, independently invented booking list);
 * nico gets WAITLIST_ENTRY's row (contract v2.1, pin F); reese gets the
 * honest empty state the pin's UI section calls for. */
function seedReservationMember(child, today) {
  const currentPeriodKey = positionPeriodFor(today, PERIOD_ANCHOR_DAY).periodKey;
  if (child.id === WAITLIST_ENTRY.athleteId) {
    const s = resolveBooking(WAITLIST_ENTRY.sessionRef);
    const upcoming = s
      ? [
          {
            ...displaySession(s, today),
            badge: { tone: 'yellow', label: 'Waitlisted' },
            bookingId: null,
            status: 'waitlisted',
            waitlistPosition: WAITLIST_ENTRY.position,
            cancellable: false,
            athleteId: child.id,
            instructor: isSpecialistType(s.type) ? SPECIALIST_BY_ID.get(s.type)?.name ?? null : null,
            durationMinutes: s.durationMinutes ?? (SPECIALIST_BY_ID.get(s.type)?.durationMinutes ?? DEFAULT_DURATION_MINUTES),
            periodKey: periodFor(s.date, PERIOD_ANCHOR_DAY).periodKey,
            nextPeriod: periodFor(s.date, PERIOD_ANCHOR_DAY).periodKey > currentPeriodKey,
          },
        ]
      : [];
    return { athleteId: child.id, name: child.name, upcoming, past: [] };
  }
  if (child.id !== SEED_ATHLETE_ID) {
    return { athleteId: child.id, name: child.name, upcoming: [], past: [] };
  }
  const resolve = (refs) =>
    refs
      .map((ref) => {
        const s = resolveBooking(ref);
        if (!s) return null;
        return reservationRow(
          s,
          { bookingId: seedBookingId(s.id), status: 'confirmed', athleteId: child.id },
          today,
          isSpecialistType(s.type) ? null : COACH.name,
          PERIOD_ANCHOR_DAY,
          currentPeriodKey
        );
      })
      .filter(Boolean);
  const upcoming = resolve(BOOKED_UPCOMING).sort(byDateThenId);
  const past = resolve(BOOKED_PAST).sort((a, b) => -byDateThenId(a, b));
  return { athleteId: child.id, name: child.name, upcoming, past };
}

/**
 * GET /reservations (Sprint 11 pin F, contract v1.9) - the family-grouped
 * Reservations view: `{ data: { members: [{ athleteId, name, upcoming: [item],
 * past: [item] }] }, loading, error, cancel(bookingId) }`. `cancel` reuses
 * the existing cancelBooking transaction (live.js) - same one it is,
 * MySchedule's own cancel() calls, so a cancellation here and one from My
 * Schedule are indistinguishable to the rules and to every other mounted
 * hook watching 'bookings'.
 */
export function useHouseholdReservations() {
  const live = isLive();
  const today = todayISO();
  const bookingsGen = useInvalidation('bookings');

  const seedMembers = HOUSEHOLD.children.map((c) => ({
    ...seedReservationMember(c, today),
    unlimited: packageById(c.packageId)?.tokens === null, // as the live branch
  }));

  const state = useSeedResource(
    live ? null : { members: seedMembers },
    live
      ? { source: () => liveHouseholdReservations(today), deps: ['household-reservations', today, bookingsGen] }
      : undefined
  );

  const cancel = async (bookingId, opts) => {
    if (!live) return { id: bookingId, status: 'cancelled', simulated: true };
    return cancelBooking({ bookingId, ...opts }); // opts: a series cancel's { cancelledVia, silent } (cancelSeries.js)
  };

  return { ...state, cancel };
}

/**
 * GET /enrollment/form (02) - the consent copy and relationship options.
 * Legal copy is content the academy edits, not something a screen hardcodes.
 */
export function useEnrollmentForm() {
  return useSeedResource({ consents: CONSENTS, relationships: RELATIONSHIPS });
}

/**
 * The signed-in guardian's OWN enrollment request (contract v1.8, A) —
 * pinned shape: { data: { request: {...enrollmentRequests doc} | null },
 * loading, error, submit({ guardian, athletes, consents }) }. `request`
 * null means "never submitted" (NotProvisioned's "start enrollment" state);
 * a real doc's `status` is 'pending' | 'approved' | 'declined'.
 *
 * Used by a SIGNED-IN BUT UNPROVISIONED account by definition — there is no
 * users/{uid} doc yet, so this goes through live.js's
 * fetchMyEnrollmentRequest/submitMyEnrollmentRequest (raw Firebase Auth
 * identity), never fetchCurrentUser(), which would throw NOT_FOUND for
 * exactly this caller.
 *
 * Seed mode returns no request (the harness demonstrates the enrollment
 * FORM itself, via useEnrollmentForm above, not a submitted-request state)
 * and submit() is a local no-op echo, matching this file's usual seed
 * posture for write actions.
 */
export function useEnrollment() {
  const live = isLive();
  const gen = useInvalidation('enrollmentRequests');

  // `status` is surfaced at the top level ('none' when there is no request)
  // so NotProvisioned/Registration branch on one field — the frontend lane
  // coded against exactly that (PM integration reconciliation).
  const state = useSeedResource(
    live ? null : { request: null, status: 'none' },
    live
      ? {
          source: async () => {
            const request = await fetchMyEnrollmentRequest();
            return { request, status: request?.status ?? 'none' };
          },
          deps: ['enrollment', gen],
        }
      : undefined
  );

  // guardianNotes (emergency contact + medical, free text) rides the
  // request and lands in athletes/{id}/private/medical on approval — the
  // data-minimization home for it (PM integration, v1.8 amendment).
  const submit = async ({ guardian, athletes, consents, guardianNotes = null }) => {
    if (!live) return { request: { status: 'pending' }, status: 'pending', simulated: true };
    return submitMyEnrollmentRequest({ guardian, athletes, consents, guardianNotes });
  };

  return { ...state, submit };
}

/**
 * The ops/owner enrollment approval queue (contract v1.8, A + D's "Admin
 * section") — pinned shape: { data: { requests: [...pending
 * enrollmentRequests docs] }, loading, error, approve(uid), decline(uid,
 * reason) }. approve() runs the pinned one-batched-write approval (live.js's
 * approveEnrollmentRequest); decline() sets status/declineReason.
 *
 * Live-only, no seed/demo branch to keep in sync (this file's established
 * "screens must not crash with the live flag off" posture for staff-only
 * live surfaces, same as useSessionAttendance/useTournamentResults): with
 * isLive() false this resolves to an empty queue and no-op actions rather
 * than inventing a fake pending-families list.
 */
export function useEnrollmentQueue() {
  const live = isLive();
  const gen = useInvalidation('enrollmentRequests');

  const state = useSeedResource(
    live ? null : { requests: [] },
    live
      ? { source: async () => ({ requests: await fetchPendingEnrollmentRequests() }), deps: ['enrollment-queue', gen] }
      : undefined
  );

  const approve = async (uid) => {
    if (!live) return { uid, status: 'approved', simulated: true };
    return approveEnrollmentRequest(uid);
  };
  const decline = async (uid, reason) => {
    if (!live) return { uid, status: 'declined', simulated: true };
    return declineEnrollmentRequest(uid, reason);
  };

  return { ...state, approve, decline };
}

/**
 * GET /packages (02 step 3, and later 10 and 15).
 *
 * The catalogue reads through the seam like everything else: the handoff's
 * state list has `tiers[]` arriving from the API, and a hardcoded import is a
 * screen that cannot survive a price change without a deploy.
 *
 * Contract v2.0 pin A: ONE catalogue - `golf`/`dropIn`/`fitness`/`elite`
 * (four lists) collapses to `tokens` (the four token packages) plus
 * `elite`/`single` (each one package). PAYLOAD SHAPE CHANGE - flagged for
 * the PM/frontend lane. (No live caller remains: Registration reads
 * ALL_PACKAGES directly and the standalone PackageStep screen was deleted in
 * the 2026-09-17 repo cleanup.)
 */
export function usePackages() {
  return useSeedResource({
    tokens: TOKEN_PACKAGES,
    elite: ELITE,
    single: SINGLE_TOKEN,
  });
}

/**
 * GET /coach/blocks?date=today (12).
 *
 * Live (QA re-sweep #6/#7/N4 — this hook had NO live branch, so the coach's
 * Overview/Sessions/attendance all ran on seed fixtures and every block's
 * sessionId was null): blocks are TODAY'S real sessions. Every block carries
 * its real `sessionId`, which is what makes the attendance thread-through
 * and useSessionAttendance's writes real. Coach assignment on sessions is
 * still null in real data (one-coach academy), so the day view is today's
 * schedule rather than an assignment-filtered subset; `expected` is the
 * session's real booked count, and bay is never invented.
 */
export function useCoachDay({ variant = 'today' } = {}) {
  const live = isLive();
  const today = todayISO();
  // Sprint 10 (pin I): Overview's counts (booked/capacity per block) are
  // stale after a booking or a cancellation elsewhere until this bumps -
  // the same post-write-refresh discipline every other live hook here uses.
  const sessionsGen = useInvalidation('sessions');
  const bookingsGen = useInvalidation('bookings');

  const blocks =
    variant === 'concurrent'
      ? COACH_BLOCKS_CONCURRENT
      : variant === 'none'
      ? []
      : COACH_BLOCKS;

  const liveCoachDay = async () => {
    const profile = await fetchCurrentUser();
    // The next day that actually has sessions — today when today does (the
    // in-season case), otherwise the upcoming session day, so a pre-season
    // coach sees their real next working day instead of months of "off".
    // Group flow only (surface-scan follow-through, PM integration): Phil's
    // and Yannick's slots are not the golf coach's blocks — without this
    // filter Luke's Today overview listed Phil's sessions as his own.
    const upcoming = (await fetchSessions(today, 1)).filter(
      (s) => s.status !== 'cancelled' && isGroupBookable(s)
    );
    const dayISO = upcoming[0]?.date ?? today;
    const sessions = upcoming.filter((s) => s.date === dayISO);
    const isToday = dayISO === today;
    const nowMinutes = new Date().getHours() * 60 + new Date().getMinutes();
    const toMinutes = (t) => parseTimeToMinutes(t) ?? 0;
    return {
      coach: { name: profile.displayName ?? 'Coach', date: isToday ? today : `Next session day · ${dayISO}` },
      blocks: sessions
        .map((s) => {
          const start = toMinutes(s.time);
          // Not start + 60: a two-hour Saturday session would flip to
          // 'closed' an hour early, taking its Start roster button with it.
          const runs = s.durationMinutes ?? DEFAULT_DURATION_MINUTES;
          const status = !isToday
            ? 'next'
            : nowMinutes >= start + runs ? 'closed' : nowMinutes >= start ? 'now' : 'next';
          return {
            id: s.id,
            sessionId: s.id,
            time: s.time,
            type: s.type,
            name: s.label || genericSessionName(s.type),
            meta: `${s.booked ?? 0} of ${s.capacity ?? '—'} booked`,
            status,
            // The coach's day rows carry no `date` of their own (the day's ISO
            // lives in `coach.date` as prose), so the group is resolved here,
            // where the raw session still has both halves.
            ageGroup: ageGroupFor(s),
            durationMinutes: runs,
            // 2026-10-02: a reserved block's handicap limit, so the coach's
            // day and the roster it opens carry the same tag families see.
            maxHandicap: handicapLimitOf(s),
          };
        }),
      concurrent: false,
      attention: [], // no live signal to derive this from yet — never invented
      outstanding: [],
    };
  };

  return useSeedResource(
    live
      ? null
      : {
          coach: COACH,
          blocks,
          concurrent: variant === 'concurrent',
          attention: ATTENTION_LIST,
          outstanding: COACH_OUTSTANDING,
        },
    live ? { source: liveCoachDay, deps: ['coach-day', today, sessionsGen, bookingsGen] } : undefined
  );
}

/**
 * GET /coach/roster (Sprint 5) - every athlete assigned to the coach, a real
 * roster rather than one session's attendance (that stays useRoster, screen
 * 13). Live: athletes where coachId == the signed-in coach's uid - the
 * equality filter firestore.rules can prove on a list read.
 */
export function useCoachRoster() {
  const live = isLive();

  const liveCoachRoster = async () => {
    const profile = await fetchCurrentUser();
    const athletes = await fetchCoachAthletes(profile.uid);
    const today = todayISO();
    return athletes
      .slice()
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .map((a) => ({
        id: a.id,
        name: a.name,
        // The owner's rule (2026-10-28): the requirement today, for everyone.
        meta: hideContractParts(`${contractMinutesFor(a, today)} min a day`),
      }));
  };

  return useSeedResource(
    live ? null : ROSTER.map((r) => ({ ...r, meta: hideContractParts(r.meta) })),
    live ? { source: liveCoachRoster, deps: ['coach-roster'] } : undefined
  );
}

/**
 * GET /coach/roster/:sessionId - the session header only; marks live in useRoster.
 *
 * Reads the block out of the generated season rather than a hand-written
 * constant, so the roster header matches what the schedule actually says is
 * running: same date, same block order, same capacity.
 */
export function useSession({ today = todayISO(), blockIndex = 1 } = {}) {
  const onDate = SEASON_BY_DATE.get(today) ?? [];
  // Clamp rather than fall back: a day with fewer blocks than the requested
  // index (a holiday-tournament day has one) must not label its only session
  // "Block 2 of 1".
  const index = Math.min(blockIndex, Math.max(0, onDate.length - 1));
  const session = onDate[index];

  // Built unconditionally - a bare `return` before useSeedResource would make
  // this a conditional hook call.
  const value = session
    ? {
        id: session.id,
        type: session.type,
        blockLabel: `Block ${index + 1} of ${onDate.length}`,
        name: session.label || genericSessionName(session.type),
        meta: `${blockRange(session.time)} · ${session.capacity} capacity · ${ROSTER.length} expected`,
        startsIn: SESSION.startsIn,
      }
    : SESSION;

  return useSeedResource(value);
}

/**
 * "4:00 PM" -> "4:00-5:00 PM", "12:30 PM" -> "12:30-1:30 PM",
 * "11:30 AM" -> "11:30-12:30 PM". Blocks are one hour, so the end keeps the
 * start's minutes and the meridiem flips across noon/midnight.
 */
function blockRange(time) {
  const [clock] = time.split(' ');
  const [, m] = clock.split(':').map(Number);
  const start24 = parseTimeToMinutes(time) ?? 0;
  const end24 = (start24 + 60) % (24 * 60);
  const endH24 = Math.floor(end24 / 60);
  const endH = endH24 % 12 === 0 ? 12 : endH24 % 12;
  const endMeridiem = endH24 < 12 ? 'AM' : 'PM';
  const mm = String(m).padStart(2, '0');
  return `${clock}-${endH}:${mm} ${endMeridiem}`;
}

/**
 * Badge/line/hint for a contract month built from real logs (Sprint 6, QA
 * #3/#4) — the live counterpart to contractFor()'s per-demo-variant copy
 * below. Shared by liveAthleteDashboard's mini card and liveContract's full
 * screen so the two read the same state off the same
 * buildContractMonthFromLogs() result and can never disagree, mirroring the
 * seed code's own stated goal for its variant-keyed `state` object.
 *
 * `kind` (contract-buffer ruling, 2026-09-30), checked in this order:
 * notStarted | ended (no badge, just the line) | behind (more than
 * BEHIND_BUFFER_DAYS missed) | complete | catchup (1-5 missed, still on
 * track) | ontrack. The screens key their styling off `kind`, never off the
 * demo variant alone.
 */
export function liveContractState(m) {
  if (m.notStarted) {
    return {
      kind: 'notStarted',
      badge: null,
      line: `Your contract starts ${longDayLabel(m.startISO)}. Days before then don't count for or against you.`,
      hint: 'Weekends are not contract days.',
    };
  }
  if (m.ended) {
    return {
      kind: 'ended',
      badge: null,
      line: `This season's contract ended ${longDayLabel(m.endISO)}.`,
      hint: null,
    };
  }
  if (m.behind) {
    return {
      kind: 'behind',
      badge: { tone: 'red', label: 'Behind' },
      line: `${m.missed} day${m.missed === 1 ? '' : 's'} behind with ${m.daysLeft} contract day${
        m.daysLeft === 1 ? '' : 's'
      } left. Every remaining day has to be logged to make the Commitment Board.`,
      hint: 'Missed a day? Tap it in the grid to add a late entry.',
    };
  }
  if (m.contractDays > 0 && m.logged === m.contractDays) {
    return {
      kind: 'complete',
      badge: { tone: 'yellow', label: 'Complete' },
      line: `All ${m.contractDays} contract days logged. You are on ${m.month}’s Commitment Board.`,
      hint: 'Weekends are not contract days.',
    };
  }
  if (m.missed > 0) {
    return {
      kind: 'catchup',
      badge: { tone: 'green', label: 'On track' },
      line: `${m.logged} of ${m.dueSoFar} days due so far — ${m.missed} to catch up. Tap a missed day to add a late entry.`,
      hint: 'Missed a day? Tap it in the grid to add a late entry.',
    };
  }
  return {
    kind: 'ontrack',
    badge: { tone: 'green', label: 'On track' },
    line: `${m.logged} of ${m.dueSoFar} days due so far. ${m.daysLeft} contract day${
      m.daysLeft === 1 ? '' : 's'
    } left — one miss still keeps the month.`,
    hint: 'One tap. Nothing else on this screen needs typing.',
  };
}

/**
 * The athlete's contract window for buildContractMonthFromLogs
 * (contract-buffer ruling, 2026-09-30): from the later of the season's first
 * day and athletes.contractStart (the Chicago date the tier was first set;
 * absent -> the season start, right for everyone enrolled pre-season) to the
 * season's last day. A malformed contractStart is ignored rather than
 * trusted - it would otherwise throw in longDayLabel.
 */
function contractWindowFor(a) {
  const start = a?.contractStart;
  const valid = typeof start === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(start);
  return {
    startISO: valid && start > SEASON_BOUNDS.start ? start : SEASON_BOUNDS.start,
    endISO: SEASON_BOUNDS.end,
  };
}

/**
 * Live payload for useAthleteDashboard (Sprint 6, QA #3): allowance and next
 * session derived from real bookings — nothing invented, none upcoming ->
 * null, matching the screen's own empty state rather than fabricating a
 * session. The contract mini-card reuses buildContractMonthFromLogs, the
 * same builder liveContract() (below) uses for the full screen.
 */
async function liveAthleteDashboard(today) {
  const ctx = await liveAthleteContext();
  const active = ctx.bookings.filter((b) => b.status !== 'cancelled');
  const upcoming = active.filter((b) => b.date >= today).sort(byDateThenId);

  // Sprint 20 (load time): the reads that follow the context are
  // independent of each other - one round trip, not four.
  const [nextSessions, logs, tokens] = await Promise.all([
    upcoming.length ? fetchSessionsByIds([upcoming[0].sessionId]) : Promise.resolve([]),
    fetchContractLogs(ctx.athlete.id),
    homeTokens(ctx.athlete, ctx.pkg, ctx.bookings, normalizeAnchorDay(ctx.household?.periodAnchorDay), today),
  ]);

  let nextSession = null;
  if (upcoming.length) {
    const sessionsById = new Map(nextSessions.map((s) => [s.id, s]));
    const s = sessionsById.get(upcoming[0].sessionId);
    nextSession = s ? displaySession(s, today) : null;
  }

  // The owner's rule (2026-10-28): the card is always built, each day judged
  // against that day's requirement (contractMinutesFor).
  const minutesByDate = new Map(logs.map((l) => [l.date, l.minutes || 0]));
  const m = buildContractMonthFromLogs({
    today, minutesByDate, contractMinutes: (iso) => contractMinutesFor(ctx.athlete, iso), ...contractWindowFor(ctx.athlete),
  });
  const state = liveContractState(m);
  const contract = {
    logged: m.logged,
    total: m.dueSoFar,
    month: m.month,
    pct: m.dueSoFar ? Math.round((m.logged / m.dueSoFar) * 100) : 0,
    line: state.line,
    // The card's badge is the Contract screen's own pill (K33: it was a
    // hard-coded "On track" that contradicted a Behind contract).
    badge: state.badge,
    kind: state.kind,
  };

  return {
    athlete: {
      // Firestore stores one `name` field (no first/full split) - both keys
      // carry the same real value rather than guessing a split.
      name: ctx.athlete.name,
      fullName: ctx.athlete.name,
      date: longDayLabel(today),
      billingStatus: billingStatusOf(ctx.athlete),
      // `allowance` -> `tokens` (contract v2.0): the AthleteDashboard
      // allowance card becomes the tokens card (UI pin).
      tokens,
    },
    nextSession,
    contract,
    // No demo "new athlete" onboarding checklist concept in live mode.
    onboarding: null,
    codeOfGrit: CODE_OF_GRIT,
  };
}

/** GET /athletes/:id + next session + contract summary (03). */
export function useAthleteDashboard({ variant = 'populated', today = todayISO(), practice = false } = {}) {
  // Practice pins the seed branch (onboarding invariant; QA 2026-09-08: the
  // walkthrough's dashboard preview errored while signed out).
  const live = !practice && isLive();
  // Post-write invalidation seam (Sprint 6 pin): a booking or a contract log
  // write changes this card - re-run after either bumps. Sprint 10:
  // 'athletes' too - the contract's one-time stamp (useContract's
  // ensureContractStart) writes contractStart, which opens the window.
  const bookingsGen = useInvalidation('bookings');
  const contractLogsGen = useInvalidation('contractLogs');
  const athletesGen = useInvalidation('athletes');
  const demo = demoOpts(variant, "Your dashboard didn't load.");

  // The next session is the athlete's first booked reference, resolved against
  // the season - the old seed invented "The Lab · Sim 2 · Luke" wholesale.
  const firstRef = BOOKED_UPCOMING[0];
  const resolved = variant === 'populated' && firstRef ? resolveBooking(firstRef) : null;
  const nextSession = resolved ? displaySession(resolved, today) : null;

  // The contract summary derives from the same real-month build the Contract
  // screen uses, so the dashboard card and the full screen cannot disagree.
  const summary = variant === 'new' || demo ? null : contractFor('ontrack', today);

  return useSeedResource(
    demo || live
      ? null
      : {
          athlete: { ...ATHLETE, billingStatus: 'active' },
          nextSession,
          contract: summary
            ? {
                logged: summary.stats.logged,
                total: summary.stats.dueSoFar,
                month: summary.month.name,
                pct: summary.stats.dueSoFar
                  ? Math.round((summary.stats.logged / summary.stats.dueSoFar) * 100)
                  : 0,
                line: summary.state.line,
                badge: summary.state.badge,
                kind: summary.state.kind,
              }
            : null,
          onboarding: variant === 'new' ? ONBOARDING : null,
          codeOfGrit: CODE_OF_GRIT,
        },
    demo ??
      (live
        ? {
            source: () => liveAthleteDashboard(today),
            deps: ['athlete-dashboard', today, bookingsGen, contractLogsGen, athletesGen],
          }
        : undefined)
  );
}

/** GET + POST /athletes/:id/commitment-contract (07). */
/**
 * The contract month for a demo state, built from the real current month.
 * date-fns/FullCalendar own the calendar shape; this only decides which due
 * days read as missed for each state and writes the copy from the numbers.
 */
function contractFor(variant, today) {
  const missedDates =
    variant === 'behind'
      ? pickDueDates({ today, count: 6, spread: 2 })
      : variant === 'ontrack'
      ? pickDueDates({ today, count: 1, spread: 4 })
      : [];

  const m = buildContractMonth({
    today,
    missedDates,
    completeAll: variant === 'complete',
    minutesPerDay: 20, // the owner's rule (2026-10-28): 20 min a day
  });

  const state = {
    ontrack: {
      kind: 'ontrack',
      badge: { tone: 'green', label: 'On track' },
      line: `${m.logged} of ${m.dueSoFar} days due so far. ${m.daysLeft} contract days left — one miss still keeps the month.`,
      hint: 'One tap. Nothing else on this screen needs typing.',
    },
    behind: {
      kind: 'behind',
      badge: { tone: 'red', label: 'Behind' },
      line: `${m.missed} days behind with ${m.daysLeft} contract days left. Every remaining day has to be logged to make the Commitment Board.`,
      hint: 'Missed a day? Tap it in the grid to add a late entry.',
    },
    complete: {
      kind: 'complete',
      badge: { tone: 'yellow', label: 'Complete' },
      line: `All ${m.contractDays} contract days logged. You are on ${m.month}’s Commitment Board.`,
      hint: 'Weekends are not contract days.',
    },
  }[variant];

  // Sprint 5 ruling: closures are schedule facts, not practice facts, and no
  // longer excuse a contract day - the caption no longer calls one out.
  const caption = 'Weekends are not contract days.';

  return {
    month: { label: m.label, name: m.month, start: m.start },
    dayStates: m.dayStates,
    stats: {
      logged: m.logged,
      contractDays: m.contractDays,
      dueSoFar: m.dueSoFar,
      missed: m.missed,
      daysLeft: m.daysLeft,
      streak: m.streak,
      minutes: m.minutes,
    },
    state,
    caption,
  };
}

/**
 * Live payload for useContract (Sprint 6, QA #4): dayStates/stats built from
 * real contractLogs via buildContractMonthFromLogs, consistent with
 * usePracticeLog's own live totals (both read fetchContractLogs for the same
 * athlete). The owner's rule (2026-10-28; spec 4.3): no tier picker - every
 * athlete is on the contract, each day judged against contractMinutesFor on
 * that day (a 12th birthday mid-month leaves earlier days alone).
 * `tierMinutes` and `required` are the requirement today (the screen's copy
 * reads `tierMinutes`); `needsStart` is true while the stored contractMinutes
 * is null, i.e. the one-time stamp (ensureContractStart) has not run yet.
 * `athleteId` null is the signed-in athlete; a string is the parent path.
 */
async function liveContract(today, athleteId) {
  const { athlete } = await liveAthleteFor(athleteId);
  const required = (iso) => contractMinutesFor(athlete, iso);
  const logs = await fetchContractLogs(athlete.id);
  const minutesByDate = new Map(logs.map((l) => [l.date, l.minutes || 0]));
  const m = buildContractMonthFromLogs({ today, minutesByDate, contractMinutes: required, ...contractWindowFor(athlete) });
  return {
    month: { label: m.label, name: m.month, start: m.start },
    dayStates: m.dayStates,
    stats: {
      logged: m.logged,
      contractDays: m.contractDays,
      dueSoFar: m.dueSoFar,
      missed: m.missed,
      daysLeft: m.daysLeft,
      streak: m.streak,
      minutes: m.minutes,
    },
    state: liveContractState(m),
    // Sprint 5 ruling: closures are schedule facts, not practice facts, and no
    // longer excuse a contract day.
    caption: 'Weekends are not contract days.',
    tierMinutes: required(today),
    required: required(today),
    needsStart: athlete.contractMinutes == null,
  };
}

export function useContract({ variant = 'ontrack', today = todayISO(), practice = false, athleteId = null } = {}) {
  // Practice pins the seed branch (onboarding invariant — the third hook to
  // need this pin; QA 2026-09-08: the walkthrough's log step showed
  // "CONTRACT DIDN'T LOAD" because this fetched live while signed out).
  const live = !practice && isLive();
  // Post-write invalidation seam (Sprint 6 pin): re-run after any
  // contractLogs write, not just one made through this hook instance.
  const contractLogsGen = useInvalidation('contractLogs');
  // 'athletes' too: ensureContractStart below writes contractMinutes and
  // contractStart, which flips needsStart and opens the contract window -
  // the same "post-write refresh" defect class Sprint 6 was cut to fix.
  const athletesGen = useInvalidation('athletes');

  const state = useSeedResource(
    live ? null : { ...contractFor(variant, today), tierMinutes: 20, required: 20, needsStart: false },
    live
      ? { source: () => liveContract(today, athleteId), deps: ['contract', today, athleteId, contractLogsGen, athletesGen] }
      : undefined
  );

  /**
   * The one-time stamp (owner's rule, 2026-10-28; spec 4.3): the first open
   * of the contract writes the derived requirement into contractMinutes and
   * today into contractStart (setContractTier's `start`), only while the
   * stored contractMinutes is null - the rules allow contractStart on that
   * transition alone. A stored value (a legacy 45 or 90, or an earlier
   * stamp) is left alone: `{ athleteId, skipped: true }`. The athlete is
   * re-read so a stamp from another tab is never written twice. Seed and
   * practice mode persist nothing.
   */
  const ensureContractStart = async () => {
    if (!live) return { simulated: true };
    const { athlete } = await liveAthleteFor(athleteId);
    if (athlete.contractMinutes != null) return { athleteId: athlete.id, skipped: true };
    return setContractTier({ athleteId: athlete.id, minutes: contractMinutesFor(athlete, today), start: true });
  };

  return { ...state, ensureContractStart };
}

/**
 * Live payload for usePracticeLog - this cycle's logged minutes, derived
 * client-side from an unfiltered per-athlete query (fetchContractLogs), the
 * same pattern deriveTokens() uses for bookings: no stored counter to
 * drift, no composite index to provision.
 */
async function livePracticeLog(today, athleteId) {
  const { athlete } = await liveAthleteFor(athleteId);
  const logs = await fetchContractLogs(athlete.id);
  const cycleStart = `${today.slice(0, 7)}-01`;
  const cycleLogs = logs.filter((l) => l.date >= cycleStart && l.date <= today);
  return {
    totalMinutes: cycleLogs.reduce((sum, l) => sum + (l.minutes || 0), 0),
    loggedToday: logs.some((l) => l.date === today),
    // Today's already-logged minutes: a second entry the same day ADDS to
    // this (a morning session plus an evening session is one day's total) —
    // the original replace-on-relog silently discarded earlier practice.
    todayMinutes: logs.find((l) => l.date === today)?.minutes ?? 0,
    // The requirement today (owner's rule, 2026-10-28), never the stored tier.
    contractMinutes: contractMinutesFor(athlete, today),
  };
}

/**
 * POST /athletes/:id/contract-logs (Sprint 5, contract v1.3) - logs a real
 * practice day with a real minutes value, replacing the fixed-tier-only tap
 * the Commitment Contract screen has today. Additive to the {data, loading,
 * error} contract: `logPractice({ minutes })` writes one contractLogs doc
 * (id `{athleteId}_{date}`) via live.js's createContractLog, snapshotting
 * `contractMinutes` = contractMinutesFor(athlete, that date) (the owner's
 * rule, 2026-10-28) so a birthday or a later rule change cannot rewrite
 * history. Fulfilled = minutes >= contractMinutes - extra minutes never bank
 * extra days. `athleteId` null logs for the signed-in athlete; a string
 * logs for that athlete (a parent for their kid; rules: logParentOwns).
 *
 * Seed mode keeps the logged entry in this hook's own component state - the
 * same simulation the contract screen already does for onboarding practice,
 * carrying a real minutes value instead of a bare logged/not-logged flag.
 * Nothing here writes live unless isLive().
 *
 * Sprint 6 pin ("post-write refresh"): the ad hoc local refreshKey this hook
 * used to bump itself after its own write is now the shared invalidation
 * seam (./invalidate) — createContractLog bumps 'contractLogs' itself, so
 * this hook (and useContract, and useAthleteDashboard's contract card) all
 * re-run together, not just whichever one made the write.
 */
export function usePracticeLog({ today = todayISO(), practice = false, athleteId = null } = {}) {
  // Practice pins the seed branch (onboarding invariant): no live query, and
  // logPractice stays a local no-op-persist — never a contractLogs write.
  const live = !practice && isLive();
  const [seedEntry, setSeedEntry] = useState(null); // { date, minutes } | null
  const contractLogsGen = useInvalidation('contractLogs');

  const SEED_CONTRACT_MINUTES = 20; // the owner's rule for the seed athlete (Jordan, 14)
  const seedLoggedToday = Boolean(seedEntry && seedEntry.date === today);
  const seedValue = {
    totalMinutes: seedLoggedToday ? seedEntry.minutes : 0,
    loggedToday: seedLoggedToday,
    // Same field the live payload carries — the footer's day total read
    // this and got nothing in seed mode (code review 2026-09-04, finding 4).
    todayMinutes: seedLoggedToday ? seedEntry.minutes : 0,
    contractMinutes: SEED_CONTRACT_MINUTES,
  };

  const state = useSeedResource(
    live ? null : seedValue,
    live
      ? { source: () => livePracticeLog(today, athleteId), deps: ['practice-log', today, athleteId, contractLogsGen] }
      : undefined
  );

  const logPractice = async ({ minutes, date }) => {
    // A second entry the same day ACCUMULATES (owner's report, 2026-09-01:
    // "my time logged resets on every entry"). `minutes` is the DELTA; the
    // day total comes back in the result. Live accumulation happens INSIDE
    // createContractLog's transaction — adding from this hook's last-loaded
    // snapshot let a fast second save overwrite from a stale base (code
    // review 2026-09-04, finding 3).
    //
    // `date` (owner's report 2026-09-10: "late entry isn't working"): an
    // optional target day, defaulting to today — the contract calendar's
    // "Add late entry" logs a PAST date through this exact same path, and
    // the rules already accept any date (the id pins athleteId+date, never
    // "today"; DATA-MODEL: any date is loggable).
    const target = date ?? today;
    if (!live) {
      // Seed keeps its single-entry demo record. A late entry for another
      // day is acknowledged but not tracked — seed's calendar comes from
      // the static month builder, so there is nothing for it to repaint.
      if (target !== today) return { date: target, minutes };
      const already = seedLoggedToday ? seedEntry.minutes : 0;
      const entry = { date: today, minutes: Math.min(720, already + minutes) };
      setSeedEntry(entry);
      return entry;
    }
    const { athlete } = await liveAthleteFor(athleteId);
    // createContractLog bumps the 'contractLogs' generation itself on
    // success - this hook's own subscription above picks that up and
    // re-runs, so there is nothing to bump here directly.
    return createContractLog({
      athleteId: athlete.id,
      date: target,
      minutes,
      contractMinutes: contractMinutesFor(athlete, target),
    });
  };

  // Remove one day's log outright (owner's report 2026-09-10: "Remove
  // entry" was inert). Live: deletes contractLogs/{athleteId}_{date} — the
  // delete bumps 'contractLogs' so every contract surface repaints. Seed:
  // clears the local entry when it matches, same no-op-persist posture as
  // logPractice above.
  const removeLog = async ({ date }) => {
    if (!date) return null;
    if (!live) {
      if (seedEntry?.date === date) setSeedEntry(null);
      return { date };
    }
    const { athlete } = await liveAthleteFor(athleteId);
    return deleteContractLog({ athleteId: athlete.id, date });
  };

  return { ...state, logPractice, removeLog, totalMinutes: state.data?.totalMinutes ?? 0 };
}

/**
 * Live payload for useAthleteDetail. Attendance history, the Commitment
 * Board count and month-over-month contract history are not sourced live
 * this sprint - there is no attendance-marking write path yet (see
 * useRoster.js) and no aggregation over contractLogs - so those fields are
 * placeholders ('—'), never invented numbers, and `hasEnoughData` stays true
 * so a long-enrolled real athlete does not get told they are new.
 */
async function liveAthleteDetail(athleteId) {
  // Three rounds, not seven sequential reads (perf wave B): the athlete and
  // the viewer are independent; everything in round two needs only those
  // two; the sessions join needs the bookings.
  const [athlete, viewer] = await Promise.all([fetchAthlete(athleteId), fetchCurrentUser()]);
  // Sprint 20 (spec 3.2): the athlete card's "Login: none / not claimed /
  // claimed <date>" line. The household parent and ops/owner can read the
  // invite; a coach cannot (fetchLoginInvite returns null -> 'invited').
  const loginEmail = athlete.loginEmail ?? null;
  const [pkg, household, bookings, invite] = await Promise.all([
    athlete.packageId ? fetchPackage(athlete.packageId) : null,
    // Sprint 12 integration: the membership editor sets the household's
    // period anchor, so the payload names the household and its anchor (a
    // coach may not be allowed to read households - tolerate a denial).
    athlete.householdId ? fetchHousehold(athlete.householdId).catch(() => null) : null,
    // Everything the kid has scheduled (owner's ask, 2026-09-01): the
    // athlete's upcoming bookings joined to their sessions. The viewer
    // decides the query shape - a parent's list read is only rules-provable
    // with the household compound filter; staff query by athleteId alone.
    fetchBookings(athleteId, viewer.role === 'parent' ? { householdId: viewer.householdId } : {}),
    loginEmail ? fetchLoginInvite(loginEmail) : null,
  ]);
  const today = todayISO();
  // The owner's rule (2026-10-28): the requirement today, for every athlete.
  const subline = hideContractParts(
    [
      `${contractMinutesFor(athlete, today)} min a day`,
      pkg ? `${pkg.name} package` : null,
    ]
      .filter(Boolean)
      .join(' · ') || null
  );

  const active = bookings.filter((b) => b.status !== 'cancelled' && b.date >= today);
  active.sort(byDateThenId);
  const sessionsById = new Map(
    (await fetchSessionsByIds(active.map((b) => b.sessionId))).map((s) => [s.id, s])
  );
  const upcoming = active.map((b) => {
    const s = sessionsById.get(b.sessionId);
    return {
      id: b.sessionId,
      date: b.date,
      dayLabel: dayLabel(b.date, today),
      time: s?.time ?? null,
      name: s?.label || genericSessionName(b.type),
      status: b.status,
      maxHandicap: handicapLimitOf(s),
    };
  });

  const login = loginStateFor(loginEmail, invite);

  return {
    athlete: {
      name: athlete.name,
      subline,
      attendance: '—',
      attendanceLabel: 'attendance — not tracked live yet',
      board: '—',
      boardLabel: 'months on the Board — not tracked live yet',
      // The stored value (null until the contract's one-time stamp); the
      // requirement itself is contractMinutesFor(athlete, date) on the dob.
      contractMinutes: athlete.contractMinutes ?? null,
      dob: athlete.dob ?? null,
      // Sprint 11 (contract v1.9, E): the package pointer the AthleteDetail
      // membership editor preselects from - additive, the same way
      // contractMinutes was added above. `fitnessPackageId` is DROPPED
      // (contract v2.0, pin A: the field is retired - one package now).
      packageId: athlete.packageId ?? null,
      // Absent == active. The staff card reads it where it cannot list the
      // household (a coach): Elite includes facility access only once paid.
      billingStatus: billingStatusOf(athlete),
      // v2.0.1 (Sprint 18): the facility-access add-on and its waiver, for
      // the membership editor's toggle (locked without the waiver).
      facilityAccess: Boolean(athlete.facilityAccess),
      facilityAccessConsent: athlete.facilityAccessConsent ?? null,
      // 2026-10-02: the handicap on file (null == none) - what the staff
      // edit (setAthleteHandicap) shows and corrects.
      handicap: handicapOf(athlete),
      // Sprint 20 (spec 3.2, D9): the child's own login state.
      loginEmail,
      login,
      householdId: athlete.householdId ?? null,
      householdName: household?.name ?? null,
      periodAnchorDay: normalizeAnchorDay(household?.periodAnchorDay),
    },
    upcoming,
    history: [],
    checklist: [],
    hasEnoughData: true,
  };
}

/**
 * GET /athletes/:id (09) — athlete detail. Parent: their own linked
 * athlete(s). Staff (ops/owner/mental): any athlete. Routed by id
 * (Sprint 5): PortalRoutes reads /portal/athlete/:athleteId and passes
 * `athleteId` in as a prop - this hook never reads the route itself.
 *
 * Live whenever isLive() AND an athleteId was passed — full stop. Sprint 6
 * (QA #1, BLOCKER): this used to also fall back to seed data for any id that
 * happened to match a seed household kid id (jordan/reese/nico), which meant
 * a parent viewing Reese's real record got served Jordan's seed payload
 * whenever Reese's real athleteId string collided with the seed id. Live
 * mode now always fetches the passed athleteId; only a genuinely missing
 * athleteId (the un-migrated/harness caller) falls back to seed data. Seed
 * mode (isLive() false) is unchanged.
 */
export function useAthleteDetail({ athleteId, variant = 'populated' } = {}) {
  const live = isLive() && athleteId != null;
  const full = variant === 'populated';
  // An athletes write (the package editor, the handicap edit, the
  // contract's one-time stamp) re-runs this read, so the screen never keeps
  // showing the old facts until an unrelated write bumps something.
  const athletesGen = useInvalidation('athletes');
  const seedValue = {
    // householdName: the back link's label (AthleteDetail reads it here now,
    // not from a second useHousehold) - the same name useHousehold's seed shows.
    athlete: { ...ATHLETE_DETAIL, subline: hideContractParts(ATHLETE_DETAIL.subline), householdName: HOUSEHOLD.name, handicap: handicapOf(seedChildById(SEED_ATHLETE_ID)), loginEmail: null, login: { state: 'none', claimedAt: null } },
    history: full ? CONTRACT_HISTORY : [],
    checklist: full ? [] : LIMITED_DATA_CHECKLIST,
    hasEnoughData: full,
  };
  return useSeedResource(
    live ? null : seedValue,
    live
      ? { source: () => liveAthleteDetail(athleteId), deps: ['athlete-detail', athleteId, athletesGen] }
      : undefined
  );
}

/**
 * Live payload for useNotificationPrefs — NOTIFICATION_CATEGORIES' static
 * metadata (name/description/lockedChannels/footnote) overlaid with the
 * signed-in user's saved per-category channel choices
 * (users.notificationPrefs). A locked CHANNEL (billing email, K31) always
 * reads true regardless of what is saved, so a stray saved override can never
 * silently turn off a transactional email; billing push is the parent's
 * choice and reads back what they saved, as notify.js's channelAllowed does.
 */
async function liveNotificationPrefs() {
  const profile = await fetchCurrentUser();
  const saved = profile.notificationPrefs || {};
  const categories = NOTIFICATION_CATEGORIES.map((c) => ({
    ...c,
    email: channelLocked(c, 'email') ? true : saved[c.id]?.email ?? c.email,
    // A map saved before Sprint 15 carries sms, not push: the default applies.
    push: channelLocked(c, 'push') ? true : saved[c.id]?.push ?? c.push,
  }));
  return { categories, note: NOTIFICATION_NOTE, saved: false };
}

/**
 * GET/PUT /guardians/:id/notification-preferences (11) — contract v1.8, G.
 * Live adds `save(prefs)`: `prefs` must be the COMPLETE desired
 * { [categoryId]: { email, push } } map (the caller/screen merges its
 * locally-edited categories onto `data.categories` before calling — a
 * partial map would silently drop every category left out, since
 * live.js#saveNotificationPrefs replaces the whole notificationPrefs field
 * in one write). `data.saved` stays false in live mode — a real "just
 * saved" toast is the SCREEN's own local state after save() resolves
 * (frontend lane's Shared SavedToast, pin I), not a flag this hook fakes.
 * `practice` (the walkthrough's Tour step) pins the seed and a local save.
 */
export function useNotificationPrefs({ variant = 'default', practice = false } = {}) {
  const live = !practice && isLive();
  const gen = useInvalidation('users');

  const state = useSeedResource(
    live ? null : { categories: NOTIFICATION_CATEGORIES, note: NOTIFICATION_NOTE, saved: variant === 'saved' },
    live ? { source: liveNotificationPrefs, deps: ['notification-prefs', gen] } : undefined
  );

  const save = async (prefs) => {
    if (!live) return { notificationPrefs: prefs, simulated: true };
    return saveNotificationPrefs(prefs);
  };

  return { ...state, save };
}

/**
 * 'yyyy-MM-dd' -> the ISO date of that week's Monday (UTC). Self-contained
 * date math (no new data/calendar.js helper — this lane does not touch
 * data/ this sprint) built on addDaysISO, already imported here.
 */
/**
 * The signed-in person's own profile facts for Settings: the sign-in email
 * (read-only) and the self-service phone that text notices go to. Seed: the
 * Whitfield guardian; live: the users doc plus the auth email.
 */
export function useProfile() {
  const live = isLive();
  const gen = useInvalidation('users');
  const state = useSeedResource(
    live ? null : { email: 'dana@email.com', displayName: 'Dana Whitfield', phone: null },
    live ? { source: liveProfile, deps: ['profile', gen] } : undefined
  );
  const savePhone = async (phone) => {
    if (!live) return { phone, simulated: true };
    return saveMyPhone(phone);
  };
  return { ...state, savePhone };
}

async function liveProfile() {
  const me = await fetchCurrentUser();
  return { email: me.email ?? null, displayName: me.displayName ?? null, phone: me.phone ?? null };
}

function mondayOfWeek(todayIso) {
  const dow = new Date(`${todayIso}T00:00:00Z`).getUTCDay(); // 0=Sun..6=Sat
  const diff = dow === 0 ? -6 : 1 - dow;
  return addDaysISO(todayIso, diff);
}

/**
 * Pure derivation for the Admin dashboard's "who needs a call" grouping
 * (contract v1.8, D) — no Firestore, no hooks, so it is independently
 * Node-assertable against a fabricated bookings/contractLogs/athletes array
 * (see the routing report's verification section). Reuses
 * buildContractMonthFromLogs (already imported in this file, data/calendar)
 * for (3) exactly as liveContractState/liveChildCard already do, rather than
 * re-deriving "behind" a second way.
 *
 * @param {object} args
 * @param {Array} args.athletes - every athletes doc ({id, name, contractMinutes, packageId, ...}).
 * @param {number} args.pendingCount - pending enrollmentRequests count.
 * @param {Array} args.noshowBookings - bookings with status 'noshow', date >= monthStart.
 * @param {Array} args.contractLogs - contractLogs with date >= monthStart, every athlete.
 * @param {Array|null} args.baselineAthleteIds - athleteIds with a baseline assessment window this season (any status); null when the windows could not be read, which lists nobody.
 * @param {string} args.today - ISO 'yyyy-MM-dd'.
 */
export function deriveWhoNeedsCall({
  athletes,
  pendingCount,
  noshowBookings,
  contractLogs,
  baselineAthleteIds,
  today,
}) {
  const nameById = new Map(athletes.map((a) => [a.id, a.name ?? null]));

  const noshowCounts = new Map();
  for (const b of noshowBookings) {
    noshowCounts.set(b.athleteId, (noshowCounts.get(b.athleteId) || 0) + 1);
  }
  const noshows = [...noshowCounts.entries()]
    .map(([athleteId, count]) => ({ athleteId, name: nameById.get(athleteId) ?? null, count }))
    .sort((a, b) => b.count - a.count);

  const logsByAthlete = new Map();
  for (const l of contractLogs) {
    const list = logsByAthlete.get(l.athleteId) ?? [];
    list.push(l);
    logsByAthlete.set(l.athleteId, list);
  }
  const contractBehind = [];
  for (const a of athletes) {
    // The owner's rule (2026-10-28): everyone is on the contract, each day
    // judged against that day's requirement - no stored tier to skip on.
    const logs = logsByAthlete.get(a.id) ?? [];
    const minutesByDate = new Map(logs.map((l) => [l.date, l.minutes || 0]));
    const m = buildContractMonthFromLogs({
      today, minutesByDate, contractMinutes: (iso) => contractMinutesFor(a, iso), ...contractWindowFor(a),
    });
    // Staff get the families' definition (contract-buffer ruling): Behind
    // is more than BEHIND_BUFFER_DAYS missed inside the contract window.
    if (m.behind) {
      contractBehind.push({ athleteId: a.id, name: a.name ?? null, missed: m.missed, daysLeft: m.daysLeft });
    }
  }
  contractBehind.sort((a, b) => b.missed - a.missed);

  // Assessments spec 8: "Baseline due" replaces "Diagnostic not entered" -
  // an athlete with no baseline window is due one. Unknown (null) lists
  // nobody rather than every athlete.
  const tested = new Set(baselineAthleteIds ?? []);
  const baselineDue = baselineAthleteIds == null
    ? []
    : athletes.filter((a) => !tested.has(a.id)).map((a) => ({ athleteId: a.id, name: a.name ?? null }));

  return {
    pendingEnrollment: { count: pendingCount },
    noshows,
    contractBehind,
    baselineDue,
  };
}

/**
 * Live payload for useAdminDashboard (contract v1.8, D) — built entirely
 * from existing collections, no new store. Enrolled count and
 * enrollment-by-package come from a full athletes read; block fill sums
 * booked/capacity across THIS WEEK's sessions grouped by session TYPE
 * (training/tournament/phil/mental), not by day the way the seed's
 * BLOCK_FILL mockup is — the pin's "group type only" is read here as a
 * deliberate live-mode shape choice, flagged in the routing report since it
 * diverges from BLOCK_FILL's {day, pct} shape to {type, pct}.
 *
 * `outstanding` keeps the SEED's existing row shape ({id, who, why, tag,
 * tone, packageIds}) so nothing downstream needs a shape migration, plus
 * two ADDITIVE fields (`kind`, `athleteId`) so a real tap can route
 * somewhere real (the pin's "every card TAPS to somewhere real") — flagged
 * as an additive, backward-compatible shape extension in the report.
 *
 * The existing tier filter (TIER_FILTERS/`filter`) stays a
 * seed/demo-harness-only affordance this sprint: live mode always returns
 * the unfiltered 'all tiers' view (no interactive filter setter exists on
 * this hook to drive a live re-query from) — reported as not done rather
 * than half-built.
 */
async function liveAdminDashboard(today) {
  const monthStart = `${today.slice(0, 7)}-01`;
  const weekStart = mondayOfWeek(today);
  const weekEnd = addDaysISO(weekStart, 6);

  // The assessment windows read may fail on its own (rules that do not
  // grant it yet): that drops the "Baseline due" rows, never the dashboard.
  const [allAthletes, sessionsThisWeek, pending, noshowBookings, contractLogs, windows, allHouseholds] =
    await Promise.all([
      fetchAllAthletes(),
      fetchSessionsInRange(weekStart, weekEnd),
      fetchPendingEnrollmentRequests(),
      fetchNoShowBookingsSince(monthStart),
      fetchContractLogsSince(monthStart),
      fetchAllAssessmentWindows().catch(() => null),
      fetchAllHouseholds(),
    ]);
  // Owner 2026-10-15: staging shares this database, so the test family
  // (test: true, data/testFamily.js) is left out of every count and list
  // below. It never books, so no booking or contract log names it.
  const { households, athletes } = withoutTestFamilies({ households: allHouseholds, athletes: allAthletes });

  // Contract v2.1, pin H: membership status counts + who's lapsed - absent
  // == active, the same default every other membership read in this app
  // uses. `lapsedHouseholds` links straight to nothing invented — just the
  // household's own id/name, matching what the admin screen already knows
  // how to show elsewhere.
  const membership = { active: 0, pastDue: 0, lapsed: 0, pending: 0, lapsedHouseholds: [] };
  for (const h of households) {
    const status = h.membership?.status ?? 'active';
    if (status === 'past_due') membership.pastDue += 1;
    else if (status === 'lapsed') {
      membership.lapsed += 1;
      membership.lapsedHouseholds.push({ id: h.id, name: h.name ?? null });
    } else membership.active += 1;
  }
  // Sprint 20 (spec 4.4/7): athletes still on checkout - an ATHLETE count beside the household counts, so the dashboard agrees with the sign-ups report.
  membership.pending = athletes.filter((a) => billingStatusOf(a) === 'pending').length;

  // This season's baselines only (the list's rule, data/assessmentWindows.js dueStatus): last season's do not count.
  const season = seasonOf(today);
  const baselineAthleteIds = windows == null ? null : windows.filter((w) => w.kind === 'baseline' && w.season === season).map((w) => w.athleteId);
  const who = deriveWhoNeedsCall({
    athletes,
    pendingCount: pending.length,
    noshowBookings,
    contractLogs,
    baselineAthleteIds,
    today,
  });

  const outstanding = [];
  if (who.pendingEnrollment.count > 0) {
    outstanding.push({
      id: 'enrollment-pending',
      kind: 'enrollment',
      athleteId: null,
      who: `${who.pendingEnrollment.count} pending enrollment${who.pendingEnrollment.count === 1 ? '' : 's'}`,
      why: 'Awaiting review in the enrollment queue',
      tag: 'Enrollment',
      tone: 'yellow',
      packageIds: null,
    });
  }
  for (const n of who.noshows) {
    outstanding.push({
      id: `noshow-${n.athleteId}`,
      kind: 'noshow',
      athleteId: n.athleteId,
      who: n.name ?? n.athleteId,
      why: `${n.count} no-show${n.count === 1 ? '' : 's'} this month`,
      tag: 'Attendance',
      tone: 'red',
      packageIds: null,
    });
  }
  for (const c of who.contractBehind) {
    outstanding.push({
      id: `contract-${c.athleteId}`,
      kind: 'contract',
      athleteId: c.athleteId,
      who: c.name ?? c.athleteId,
      why: `Contract behind — ${c.missed} day${c.missed === 1 ? '' : 's'} missed, ${c.daysLeft} left`,
      tag: 'Contract',
      tone: 'yellow',
      packageIds: null,
    });
  }
  for (const d of who.baselineDue) {
    outstanding.push({
      id: `baseline-${d.athleteId}`,
      kind: 'baseline',
      athleteId: d.athleteId,
      who: d.name ?? d.athleteId,
      why: 'Baseline due',
      tag: 'Assessment',
      tone: 'yellow',
      packageIds: null,
    });
  }

  const byPackage = new Map();
  for (const a of athletes) {
    if (!a.packageId) continue;
    byPackage.set(a.packageId, (byPackage.get(a.packageId) || 0) + 1);
  }
  const enrollment = [...byPackage.entries()].map(([id, count]) => ({
    id,
    name: packageById(id)?.name ?? id,
    athletes: count,
  }));

  // Block fill is PER DAY (Mon..Sat, the seed BLOCK_FILL shape the admin
  // bars render) over GROUP sessions only — the pin's "group type only"
  // meant "exclude specialist slots", not "group by type" (PM integration
  // reconciliation of the routing lane's flagged reading).
  const fillByDay = new Map(['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => [d, { booked: 0, capacity: 0 }]));
  let totalBooked = 0;
  let totalCapacity = 0;
  // 2026-10-02: the week's handicap-reserved blocks, named beside the bars
  // (a bar is a whole day, so the tag cannot ride on it) - same read.
  const reservedBlocks = [];
  for (const s of sessionsThisWeek) {
    if (s.status === 'cancelled' || !isGroupBookable(s)) continue;
    const day = fillByDay.get(shortWeekday(s.date));
    if (!day) continue; // Sunday: no group blocks by design
    day.booked += s.booked ?? 0;
    day.capacity += s.capacity ?? 0;
    totalBooked += s.booked ?? 0;
    totalCapacity += s.capacity ?? 0;
    if (handicapLimitOf(s) != null) {
      reservedBlocks.push({
        sessionId: s.id,
        date: s.date,
        day: shortWeekday(s.date),
        time: s.time ?? null,
        maxHandicap: handicapLimitOf(s),
        booked: s.booked ?? 0,
        capacity: s.capacity ?? 0,
      });
    }
  }
  reservedBlocks.sort((a, b) => a.date.localeCompare(b.date) || (parseTimeToMinutes(a.time) ?? 0) - (parseTimeToMinutes(b.time) ?? 0));
  const blockFill = [...fillByDay.entries()].map(([day, v]) => ({
    day,
    pct: v.capacity ? Math.round((v.booked / v.capacity) * 100) : 0,
  }));

  return {
    outstanding,
    metrics: {
      enrolled: athletes.length,
      fill: totalCapacity ? `${Math.round((totalBooked / totalCapacity) * 100)}%` : '—',
      fillLabel: 'average block fill this week',
      enrolledLabel: 'enrolled athletes',
    },
    enrollment,
    highlightPackage: null,
    blockFill,
    reservedBlocks,
    filter: TIER_FILTERS[0],
    weekLabel: `Week of ${longDayLabel(weekStart)}`,
    membership,
  };
}

/**
 * GET /admin/fitness-completion + enrollment + block fill (15).
 *
 * The filter cuts by package, matching a row's packageIds against the filter
 * id. Org-level rows (packageIds: null) survive every filter — that work still
 * exists whichever tier Phil is looking at. The enrolled count follows the
 * filter so the header's number and the stat card cannot disagree; block fill
 * is facility-wide and cannot be cut by tier, which the screen says.
 *
 * Live (contract v1.8, D): see liveAdminDashboard above. Subscribed to every
 * collection its derivation reads from, so an approval, a no-show mark, a
 * contract log, or a diagnostic publish anywhere all refresh this screen.
 */
export function useAdminDashboard({ variant = 'populated' } = {}) {
  const live = isLive();
  const today = todayISO();
  const filter = variant === 'filtered' ? TIER_FILTERS[1] : TIER_FILTERS[0];
  const matches = (o) =>
    filter.id === 'all' || o.packageIds == null || o.packageIds.includes(filter.id);

  const athletesGen = useInvalidation('athletes');
  const bookingsGen = useInvalidation('bookings');
  const contractLogsGen = useInvalidation('contractLogs');
  const assessmentsGen = useInvalidation(ASSESSMENTS_KEY);
  const enrollmentGen = useInvalidation('enrollmentRequests');
  const householdsGen = useInvalidation('households');

  return useSeedResource(
    live
      ? null
      : {
          outstanding: OUTSTANDING.filter(matches),
          metrics:
            filter.id === 'all'
              ? ADMIN_METRICS
              : { ...ADMIN_METRICS, enrolled: filter.count, enrolledLabel: `athletes on ${filter.label.replace(' only', '')}` },
          enrollment: ENROLLMENT_BY_PACKAGE,
          highlightPackage: filter.id === 'all' ? null : filter.id,
          blockFill: BLOCK_FILL,
          // The seed season has no handicap-reserved block.
          reservedBlocks: [],
          filter,
          weekLabel: null,
          // Contract v2.1, pin H: the harness's one named seed household
          // (HOUSEHOLD/Whitfield) is active — no invented lapsed/past_due
          // household ships in the default demo state.
          membership: { active: 1, pastDue: 0, lapsed: 0, pending: 0, lapsedHouseholds: [] },
        },
    live
      ? {
          source: () => liveAdminDashboard(today),
          deps: [
            'admin-dashboard',
            today,
            athletesGen,
            bookingsGen,
            contractLogsGen,
            assessmentsGen,
            enrollmentGen,
            householdsGen,
          ],
        }
      : undefined
  );
}

/**
 * Live payload for useStaff — every users doc with staff == true, plus the
 * pending staffInvites queue (contract v1.8, E). `mfa`/`note` are never
 * invented per-account signals (no such field exists in the schema — the
 * seed's STAFF rows hand-write them as review copy) — null here, matching
 * this file's "never invented" discipline elsewhere (liveChildCard's
 * standing/age, liveAthleteDetail's attendance placeholders).
 */
async function liveStaff() {
  const [staffUsers, invites] = await Promise.all([fetchStaffUsers(), fetchPendingStaffInvites()]);
  return {
    staff: staffUsers
      .map((u) => ({
        id: u.id,
        name: u.displayName ?? u.email ?? u.id,
        role: u.role ?? null,
        specialistId: u.specialistId ?? null,
        // 2026-10-14 (assessments spec 3): absent is off.
        assessor: u.assessor === true,
        mfa: null,
        note: null,
      }))
      .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    pendingInvites: invites.map((i) => ({
      id: i.id,
      email: i.email,
      role: i.role,
      displayName: i.displayName ?? null,
      specialistId: i.specialistId ?? null,
    })),
    roles: STAFF_ROLES,
    auditNote: AUDIT_NOTE,
    screeningNote: SCREENING_NOTE,
  };
}

/**
 * GET/POST /admin/staff-accounts (16) — owner (+ ops read, contract v1.8 E)
 * only. Live: pinned shape adds `pendingInvites` (additive — the seed shape
 * has no such list since the harness never invented "provisioning" state)
 * alongside the existing `staff`/`roles`/`auditNote`/`screeningNote` fields,
 * plus `invite({ email, role, displayName, specialistId })` writing a
 * staffInvites doc (owner-only, enforced by firestore.rules).
 * scripts/provision-family.mjs (db lane) is what actually turns a pending
 * invite into a real users doc when the person first signs in — this hook
 * only ever creates the invite record.
 */
export function useStaff({ variant = 'populated' } = {}) {
  const live = isLive();
  const gen = useInvalidation('users');
  const invitesGen = useInvalidation('staffInvites');

  const state = useSeedResource(
    live
      ? null
      : {
          staff: STAFF,
          pendingInvites: [],
          roles: STAFF_ROLES,
          auditNote: AUDIT_NOTE,
          screeningNote: SCREENING_NOTE,
          adding: variant === 'add',
        },
    live ? { source: liveStaff, deps: ['staff', gen, invitesGen] } : undefined
  );

  const invite = async ({ email, role, displayName = null, specialistId = null }) => {
    if (!live) return { email, role, displayName, specialistId, status: 'pending', simulated: true };
    return createStaffInvite({ email, role, displayName, specialistId });
  };

  return { ...state, invite };
}

/**
 * Live payload for useSessionAttendance — every active booking for one
 * session, joined to the athlete's name. Read authorization is the EXISTING
 * bookings-read rule's coach clause (athleteData(resource.data.athleteId)
 * .coachId == caller) — Firestore evaluates that per candidate document for
 * list/query reads too (a get() keyed off a field the query does not itself
 * filter on is the standard "join" pattern for row-level list security), so
 * no new rules grant was needed for this hook; verified against the emulator
 * (routing report). One consequence worth knowing: on a block shared across
 * coaches, each coach's roster silently shows only their own assigned
 * athletes — consistent with "coach → assigned athletes only, by assignment,
 * never by role," not a bug.
 *
 * Per-athlete name lookups use fetchAthlete() (a single-document read,
 * already coach-readable under the same assignment rule) rather than a
 * batched by-id list query, which would need its own, unbuilt list-query
 * grant on `athletes` for the coach role.
 */
async function liveSessionAttendance(sessionId) {
  const bookings = await fetchBookingsBySession(sessionId);
  const athletes = await Promise.all(bookings.map((b) => fetchAthlete(b.athleteId)));
  const nameById = new Map(athletes.map((a) => [a.id, a.name]));
  return bookings
    .map((b) => ({
      bookingId: b.id,
      athleteId: b.athleteId,
      name: nameById.get(b.athleteId) ?? null,
      attendee: b.attendee ?? 'athlete',
      status: b.status,
      // Coach's optional no-show note (owner's report 2026-09-10) — absent
      // on most bookings, so the row carries an honest null.
      noshowReason: b.noshowReason ?? null,
    }))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}

/**
 * GET /sessions/:id/bookings + attendance marking (Sprint 6 pin, QA #6/#7) —
 * pinned shape: { data: [{ bookingId, athleteId, name, status,
 * noshowReason }], loading, error, mark(bookingId, status),
 * setReason(bookingId, reason) } (noshowReason/setReason added 2026-09-10,
 * owner's report). `status` is confirmed|attended|noshow;
 * attendance IS bookings.status (coach IN -> 'attended', OUT -> 'noshow',
 * un-marking -> back to 'confirmed' is the caller's job to decide, not this
 * hook's).
 *
 * Coach-only, sessionId-scoped, live-only surface — there is no seed/demo
 * branch to keep in sync (no session-scoped seed roster exists), matching
 * this file's existing "screens must not crash with the live flag off"
 * convention: with isLive() false or no sessionId yet, this resolves to an
 * empty list and a no-op mark() rather than touching ./live.js.
 */
export function useSessionAttendance(sessionId) {
  const live = isLive();
  // Post-write invalidation seam (Sprint 6 pin): a mark() from this hook OR
  // any other coach's re-runs every mounted instance reading bookings.
  const bookingsGen = useInvalidation('bookings');

  const state = useSeedResource(
    live && sessionId ? null : [],
    live && sessionId
      ? { source: () => liveSessionAttendance(sessionId), deps: ['session-attendance', sessionId, bookingsGen] }
      : undefined
  );

  const mark = async (bookingId, status) => {
    if (!live) return { id: bookingId, status };
    return updateBookingStatus({ bookingId, status });
  };

  // Persist the coach's no-show reason (owner's report 2026-09-10: the
  // "+ Add a reason" chip was inert). Same rules surface as mark() —
  // setBookingNoshowReason bumps 'bookings' itself, so this hook's rows
  // (and the family's views) refresh with the saved note.
  const setReason = async (bookingId, reason) => {
    if (!live) return { id: bookingId, noshowReason: reason || null };
    return setBookingNoshowReason({ bookingId, reason });
  };

  /**
   * Sprint 10 (contract v1.8, H): the session-level coach note ("Add a
   * session note" — the roster's third inert-button instance) — a field on
   * the SESSION doc, not the booking, so it takes the session's real id
   * rather than a bookingId the way mark()/setReason() do. Seed/offline
   * posture matches mark()/setReason() above.
   */
  const setSessionNote = async (targetSessionId, note) => {
    if (!live) return { id: targetSessionId, coachNote: note || null, simulated: true };
    return setSessionCoachNote({ sessionId: targetSessionId, note });
  };

  // The session's CURRENT note (and, Sprint 13, its status), so the roster
  // shows/prefills the note on a revisit rather than only after this device
  // saved one (PM integration: the frontend lane flagged the missing read
  // half), and knows whether the session is already cancelled. Its own
  // small resource — the rows above are pinned as a bare array.
  const sessionsGen = useInvalidation('sessions');
  const noteState = useSeedResource(
    live && sessionId ? null : { coachNote: null, status: 'scheduled', maxHandicap: null },
    live && sessionId
      ? {
          source: async () => {
            const s = (await fetchSessionsByIds([sessionId]))[0];
            // `status` null-safe (the same generator gap firestore.rules'
            // waitlistSessionOk/sessionCancelOk document) — absent means
            // 'scheduled', never a crash on an older/generator-seeded doc.
            // 2026-10-02: the same read carries the session's handicap limit
            // (null on an open session), for the roster's reserved tag.
            return { coachNote: s?.coachNote ?? null, status: s?.status ?? 'scheduled', maxHandicap: handicapLimitOf(s) };
          },
          deps: ['session-note', sessionId, sessionsGen],
        }
      : undefined
  );

  /**
   * Staff "Cancel session" (contract v2.1, pin E) — ops/owner only,
   * enforced by firestore.rules, not here. grace.js's cancelSession() bumps
   * bookings/sessions/graceTokens itself on success, so this hook's own
   * `state` (bookings) and `noteState` (sessions.status) both refresh
   * through the SAME invalidation seam every other write in this file uses
   * — no extra wiring here beyond calling it.
   */
  const doCancelSession = async () => {
    if (!live || !sessionId) {
      return { sessionId, cancelled: 0, graceTokensMinted: 0, simulated: true };
    }
    return cancelSession(sessionId);
  };

  return {
    ...state,
    mark,
    setReason,
    setSessionNote,
    sessionNote: noteState.data?.coachNote ?? null,
    sessionStatus: noteState.data?.status ?? 'scheduled',
    sessionMaxHandicap: noteState.data?.maxHandicap ?? null,
    cancelSession: doCancelSession,
  };
}

/**
 * Live payload for useTourStandings — every tournamentResults doc, joined to
 * athlete names and each event's session label, run through
 * data/tour.js's deriveTourStandings() (the SAME function the seed constant
 * below is computed with, so live and seed can never disagree on the math).
 *
 * Contract v1.6 (Sprint 8): a doc with no int `score` cannot be ranked and
 * is skipped defensively before anything else runs — none should exist
 * (every write is shape-checked server-side), but a read path never trusts
 * that blindly.
 *
 * Name visibility (Sprint 7 open question — resolved as contract v1.5.1,
 * TEAM.md Sprint 7 integration): results written since the amendment carry
 * the athlete's display name snapshotted at write time, so this academy-
 * public leaderboard shows every name to every role without widening the
 * athletes read matrix. deriveTourStandings prefers each row's own name;
 * the fetchAthletesByIds join below runs only for pre-amendment docs with
 * no name — an individual get() per athlete (see its doc comment in
 * ./live.js for why a batched `in` query is unsafe), where an id the
 * caller cannot read still resolves to name: null rather than failing the
 * screen. Session labels (fetchSessionsByIds) have no such limit —
 * sessions are readable by any signed-in user unconditionally. `bracket` is
 * read straight off each doc (also a write-time snapshot, contract v1.6) —
 * no join needed for it at all.
 */
async function liveTourStandings() {
  const raw = await fetchTournamentResults();
  const results = raw.filter((r) => Number.isInteger(r.score));
  const athleteIds = [...new Set(results.filter((r) => r.name == null).map((r) => r.athleteId))];
  const sessionIds = [...new Set(results.map((r) => r.sessionId))];
  const [athletes, sessions] = await Promise.all([
    fetchAthletesByIds(athleteIds),
    fetchSessionsByIds(sessionIds),
  ]);
  const nameById = new Map(athletes.map((a) => [a.id, a.name]));
  const labelById = new Map(sessions.map((s) => [s.id, s.label]));
  return deriveTourStandings(results, { nameById, labelById });
}

/**
 * GET /tour/standings (Sprint 8 pin, contract v1.6, supersedes v1.5.1) —
 * pinned shape: { data: { brackets: [{ id, label, standings: [{ athleteId,
 *   name, rank, points, events, wins }] }], events: [{ date, label,
 *   results: [{ athleteId, name, bracket, score, position }] }] (v1.6.1:
 *   an event is a DATE — all of one Saturday's blocks merged into one
 *   weekly field, see deriveTourStandings),
 *   counting: { eventsHeld, counted, drops } }, loading, error }. `counting`
 * (owner's drop-week rule, 2026-09-10) says how many of the season's weeks
 * sum into each bracket's `points` — see data/tour.js's TOUR_DROP_RATE.
 * Position is derived from `score`, never stored — see deriveTourStandings.
 *
 * Academy-wide and readable by every role (contract v1.5: "standings are
 * academy-public") — unlike every other hook in this file there is no role
 * branching on the read side; the only asymmetry is the name-resolution
 * limit documented on liveTourStandings above.
 *
 * Seed: TOUR_SEED (data/tour.js) — a believable demo season with all three
 * brackets populated, always full regardless of role. Live: subscribes to
 * the 'tournamentResults' invalidation generation, so a coach/staff
 * saveResults() anywhere refreshes every mounted standings screen (the
 * Sprint 6 post-write-refresh pattern).
 */
export function useTourStandings() {
  const live = isLive();
  const resultsGen = useInvalidation('tournamentResults');

  return useSeedResource(
    live ? null : TOUR_SEED,
    live ? { source: liveTourStandings, deps: ['tour-standings', resultsGen] } : undefined
  );
}

/**
 * Live payload for useTournamentResults(sessionId) — one tournament's
 * existing results, position included. Reuses deriveTourStandings itself
 * (data/tour.js) rather than re-deriving position/ranking a second way: the
 * results for a single sessionId are exactly one `events[]` entry, so
 * running them through the same function useTourStandings uses guarantees
 * identical math (and the same bracket-order-then-position sort the pinned
 * shape wants) with no duplicated logic.
 *
 * Contract v1.6: a doc with no int `score` is skipped defensively, same as
 * liveTourStandings. Since contract v1.5.1 each doc carries its own name
 * snapshot; the fetchAthletesByIds join (individual get()s — see the
 * name-visibility note on liveTourStandings) runs only for pre-amendment
 * docs missing one. Staff entering results already have the read access
 * that fallback join needs (mental/ops/owner unconditionally; coach's
 * assigned-roster clause), so every name is expected to resolve for the
 * caller actually using this hook to enter results.
 */
async function liveTournamentResults(sessionId) {
  const raw = await fetchTournamentResultsForSession(sessionId);
  const results = raw.filter((r) => Number.isInteger(r.score));
  const athletes = await fetchAthletesByIds(
    results.filter((r) => r.name == null).map((r) => r.athleteId)
  );
  const nameById = new Map(athletes.map((a) => [a.id, a.name]));
  const { events } = deriveTourStandings(results, { nameById });
  // v1.6.1 events are keyed by DATE, and one session's rows all share the
  // session's own date — so this is either exactly one event or none.
  const event = events[0] ?? null;
  return { results: event ? event.results : [] };
}

/**
 * GET /tournaments/:sessionId/results + result entry (Sprint 8 pin,
 * contract v1.6, supersedes v1.5) — pinned shape: { data: { results:
 * [{ athleteId, name, bracket, score, position }] }, loading, error,
 * saveResults(entries) } where entries = [{ athleteId, name, bracket,
 * score }] (name/bracket: write-time snapshots, see saveTournamentResults;
 * `position` in the read shape is DERIVED, never part of what the caller
 * sends). Coach/mental/ops/owner-only write surface (enforced in
 * firestore.rules, not here), sessionId-scoped, live-only - same "no
 * seed/demo branch to keep in sync" posture as useSessionAttendance: with
 * isLive() false or no sessionId yet, this resolves to an empty results
 * list and a no-op saveResults(), so the results-entry screen never crashes
 * in the demo/harness.
 *
 * `date` for the write is derived from the sessionId's own leading
 * YYYY-MM-DD (sessions are always `YYYY-MM-DD-<block>`) — never a second
 * fact the caller could pass out of sync with the id.
 */
export function useTournamentResults(sessionId) {
  const live = isLive();
  // Post-write invalidation seam (Sprint 6 pin, generalized here): a
  // saveResults() from this hook OR from another coach's session re-runs
  // every mounted instance reading tournamentResults, including
  // useTourStandings.
  const resultsGen = useInvalidation('tournamentResults');

  const state = useSeedResource(
    live && sessionId ? null : { results: [] },
    live && sessionId
      ? {
          source: () => liveTournamentResults(sessionId),
          deps: ['tournament-results', sessionId, resultsGen],
        }
      : undefined
  );

  const saveResults = async (entries) => {
    if (!live || !sessionId) {
      return { sessionId, count: Array.isArray(entries) ? entries.length : 0 };
    }
    const date = String(sessionId).slice(0, 10);
    // saveTournamentResults bumps 'tournamentResults' itself on success -
    // this hook's own subscription above picks that up and re-runs.
    return saveTournamentResults(sessionId, date, entries);
  };

  return { ...state, saveResults };
}

/**
 * GET .../athletes?ids=... resolved to age brackets (Sprint 8 pin, contract
 * v1.6) — feeds the SCORE entry screen's per-athlete bracket chip, computed
 * BEFORE a save so saveResults() can send the same write-time bracket
 * snapshot tournamentResults docs carry (data/tour.js's bracketFor, as of
 * SEASON_BOUNDS.start — never "today", so it always matches what the write
 * will snapshot no matter when the coach saves). Returns a plain
 * { [athleteId]: bracketId | null } map, not an array, so the screen can key
 * straight off the athleteId it is already iterating (the roster) without a
 * second lookup structure.
 *
 * Live: fetchAthletesByIds — the coach calling this already has the read
 * access an athlete-by-id join needs for their own roster (assigned coach /
 * staff clauses on the athletes matrix), same access this screen's roster
 * fetch itself relies on. An id the caller cannot read is silently dropped
 * (fetchAthletesByIds' own Promise.allSettled behavior) rather than failing
 * the whole map.
 *
 * Seed mode never fetches — `data` is `{}` synchronously, matching the pin
 * ("Seed mode: {} and never fetches"); the score-entry harness state does
 * not need real brackets to render its chips.
 *
 * `athleteIds` is an array whose identity can change every render (a new
 * literal built from the roster each time) — useSeedResource's `deps` must
 * be a stable, comparable key, so this joins the de-duped, sorted ids into
 * one string rather than depending on the array itself (which would refetch
 * every render even when the actual ids never changed).
 *
 * Hooks stay unconditional regardless of live/seed/empty-ids: useSeedResource
 * always runs here, exactly once, with its two arguments deciding which
 * branch it takes internally — never a conditional hook call.
 */
export function useAthleteBrackets(athleteIds) {
  const live = isLive();
  const ids = Array.isArray(athleteIds) ? athleteIds.filter(Boolean) : [];
  const idsKey = [...new Set(ids)].sort().join(',');
  const shouldFetch = live && idsKey.length > 0;

  const liveAthleteBrackets = async () => {
    const athletes = await fetchAthletesByIds(ids);
    const out = {};
    for (const a of athletes) out[a.id] = bracketFor(a.dob ?? null, SEASON_BOUNDS.start);
    return out;
  };

  return useSeedResource(
    shouldFetch ? null : {},
    shouldFetch ? { source: liveAthleteBrackets, deps: ['athlete-brackets', idsKey] } : undefined
  );
}
